// Corresponds to the Pydantic models in the backend
export { ProductType } from './enums';
export * from './companyReviews';

// The account's authentication provider (mirrors the backend AuthProvider enum).
// Add a new federated provider here, then wire its profile badge label in
// PROVIDER_BADGE_LABELS.
export type AuthProvider = 'native' | 'google';

// Enhanced User interface
export interface User {
  id: number;
  full_name: string;
  email: string;
  phone_number?: string;
  phone?: string; // Alias for phone_number for compatibility
  address?: string;
  is_active: boolean;
  is_superuser?: boolean;
  role: 'guest' | 'user' | 'admin';
  auth_provider?: AuthProvider;
  created_at?: string;

  // Enhanced profile fields
  profile_picture_url?: string | null;
  profile_picture_uploaded_at?: string;
  tos_accepted: boolean;
  tos_accepted_at?: string;
  tos_version?: string;
  email_marketing_opted_in_at?: string | null;
  email_marketing_prompt_pending?: boolean;
}

// Travel preferences for user registration and profile
export interface TravelPreferences {
  preferred_service_types?: string[]; // spa, chef, adventure, etc.
  preferred_accommodation_types?: string[]; // villa, penthouse, etc.
  preferred_amenities?: string[]; // pool, spa, kitchen, etc.
  travel_style?: string; // luxury, adventure, business, romantic
  min_budget_per_night?: number;
  max_budget_per_night?: number;
  preferred_locations?: string[];
}

// Full user preferences from backend
export interface UserPreferences {
  id: number;
  user_id: number;
  preferred_locations?: string[];
  preferred_accommodation_types?: string[];
  preferred_service_types?: string[];
  preferred_amenities?: string[];
  min_budget_per_night?: number;
  max_budget_per_night?: number;
  typical_trip_duration?: number;
  travel_style?: string;
  dietary_restrictions?: string[];
  accessibility_needs?: string[];
  favorite_properties?: number[];
  favorite_services?: number[];
}

// For updating user preferences
export interface UserPreferencesUpdate {
  preferred_locations?: string[];
  preferred_accommodation_types?: string[];
  preferred_service_types?: string[];
  preferred_amenities?: string[];
  min_budget_per_night?: number;
  max_budget_per_night?: number;
  typical_trip_duration?: number;
  travel_style?: string;
  dietary_restrictions?: string[];
  accessibility_needs?: string[];
}

export interface UserCreate {
  full_name: string;
  email: string;
  password: string;
  phone_number?: string;
  role?: 'guest' | 'user' | 'admin';

  // Enhanced registration fields
  profile_picture_url?: string;
  tos_accepted: boolean;
  tos_version?: string;
  preferences?: TravelPreferences;
}

export interface UserUpdate {
  full_name?: string;
  email?: string;
  phone_number?: string;
  password?: string;
  profile_picture_url?: string | null;
}

export interface UserWithStats extends User {
  total_bookings: number;
  total_spent: number;
  last_booking_date?: string;
}

export type AdminUserAccountStatus = 'active' | 'deactivated' | 'erased';
export type AdminUserRole = 'user' | 'admin' | 'guest';
export type AdminUserSort =
  'signed_up_newest' | 'signed_up_oldest' | 'last_booking_newest' | 'last_booking_oldest';

export interface AdminUserListItem {
  id: number;
  email: string;
  full_name: string | null;
  phone_number: string | null;
  role: AdminUserRole;
  auth_provider: AuthProvider;
  account_status: AdminUserAccountStatus;
  created_at: string;
  total_bookings: number;
  total_booked_cents: number;
  last_booking_at: string | null;
}

export interface AdminUserPage {
  items: AdminUserListItem[];
  total: number;
}

export interface AdminUserListQuery {
  q?: string;
  status?: AdminUserAccountStatus;
  role?: AdminUserRole;
  sort?: AdminUserSort;
  skip?: number;
  limit?: number;
}

// Enhanced Accommodation interface
/**
 * Where an Accommodation listing originated. Drives checkout flow:
 *   - 'NATIVE': Belhop-managed inventory — booking requires host approval
 *     (Request Booking flow, awaiting-host-approval notice).
 *   - 'NEXTPAX': pre-approved NextPax inventory — instant booking
 *     (Stripe payment flow on the dedicated NextPax payment route,
 *     no host approval step).
 *
 * Only applies to Accommodations. All Services are Belhop-listed (NATIVE)
 * by definition — NextPax does not surface services. See ADR-0001.
 *
 * Backend source of truth: backend/models/enums.py::ListingSource.
 */
export type ListingSource = 'NATIVE' | 'NEXTPAX';

/** How a service is used by the guest: picks a date/time, follows a fixed schedule, or has no date. */
export type SchedulingMode = 'GUEST_PICKS' | 'FIXED_SCHEDULE' | 'UNDATED';

/** One bookable fixed-schedule occurrence as a guest reads it (hidden and past rows are never returned). */
export interface ServiceOccurrence {
  readonly id: number;
  /** ISO date. */
  readonly start_date: string;
  /** "HH:MM" local wall time, or null. */
  readonly start_time: string | null;
  /** ISO date, null for a single-day occurrence. */
  readonly end_date: string | null;
  /** "HH:MM" local wall time, or null. */
  readonly end_time: string | null;
}

/** One fixed-schedule occurrence as an admin reads it, with its booking and request counts. */
export interface ServiceOccurrenceAdmin {
  id: number;
  /** ISO date. */
  start_date: string;
  /** "HH:MM" local wall time, or null. */
  start_time: string | null;
  /** ISO date, null for a single-day occurrence. */
  end_date: string | null;
  /** "HH:MM" local wall time, or null. */
  end_time: string | null;
  booking_count: number;
  request_count: number;
  /** Set once a booked occurrence was removed: kept for its bookings, hidden from guests. */
  hidden_at: string | null;
}

/** Body of an occurrence create or update; every field is sent so an edit can clear an optional one. */
export interface ServiceOccurrencePayload {
  start_date: string;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
}

/** How an occurrence removal was carried out: deleted outright, or hidden because bookings reference it. */
export type ServiceOccurrenceRemovalOutcome = 'DELETED' | 'HIDDEN';

export interface ServiceOccurrenceRemoval {
  occurrence_id: number;
  outcome: ServiceOccurrenceRemovalOutcome;
}

/** One dated window in which a guest-picks service can be booked; whole days, both ends inclusive. */
export interface ServiceBookableWindow {
  id: number;
  /** ISO date. */
  start_date: string;
  /** ISO date, on or after start_date. */
  end_date: string;
}

/** The public read of a guest-picks service's upcoming booking windows. */
export interface ServiceBookableWindows {
  /** True when the service has windows at all, even if every one has ended. */
  is_windowed: boolean;
  /** Upcoming windows only, in start-date order; empty unless `is_windowed`. */
  windows: ServiceBookableWindow[];
}

/** Body of a booking-window create or update. */
export interface ServiceBookableWindowPayload {
  start_date: string;
  end_date: string;
}

/**
 * Which charges a guest-facing price already includes. Server-computed from
 * the listing's markup and tax; the client only maps it to copy.
 * Backend source of truth: backend/models/enums.py::PriceDisclosure.
 */
export type PriceDisclosure = 'fees_and_taxes' | 'taxes_only' | 'none';

// Typed property descriptions sourced from NextPax. Native accommodations
// omit this object and fall back to the scalar `description` field.
export interface PropertyDescriptions {
  headline?: string;
  house?: string;
  interior?: string;
  area?: string;
}

// The fixed catalog of bed types a bedroom row's bed line may name.
// Mirrors backend/models/enums.py::BedType (ADR-0083).
export type BedType = 'king' | 'queen' | 'double' | 'single' | 'bunk' | 'sofa_bed' | 'crib';

// One bed type + quantity line within a bedroom row. Mirrors
// backend/schemas/bedroom.py::BedLine.
export interface BedLine {
  bed_type: BedType;
  quantity: number;
}

// A single room card for the guest "Where you'll sleep" section. Mirrors
// backend/schemas/bedroom.py::BedroomResponse. `image` is the linked
// gallery photo (the unit's own, or its parent's when the unit is an
// option), `null` when the row has no photo -- the guest card falls back
// to a bed-drawing panel. Never present on a NEXTPAX listing.
export interface BedroomDetail {
  id: number;
  name: string;
  display_order: number;
  beds: BedLine[];
  property_image_id: number | null;
  image: PropertyImage | null;
}

/** Public, deliberately imprecise area around a listing; never the exact point. */
export interface ApproximateLocation {
  lat: number;
  lng: number;
  radius_m: number;
}

export interface Accommodation {
  id: number;
  slug?: string;
  name: string;
  description: string;
  descriptions?: PropertyDescriptions;
  short_address?: string;
  location: string;
  // Null only when `is_by_request` is true (mirrors the backend CHECK
  // `ck_accommodations_price_present_unless_by_request`) — every priced
  // accommodation still carries a real number here.
  base_price_per_night: number | null;
  // Belhop's markup on the vendor price, serialised by the API as a decimal
  // string (for example "12.50").
  platform_markup_percentage: string;
  price_disclosure: PriceDisclosure;
  vendor_tax_percentage?: number; // Vendor's preferred tax percentage
  final_price_per_night?: number;
  max_guests: number;
  bedrooms?: number;
  // Native-only bed count, independent of `bedrooms` (a bedroom can hold
  // more than one bed). Never populated for a NEXTPAX listing.
  beds?: number;
  bathrooms?: number;
  square_feet?: number;
  vendor_id: number;
  type: string;
  amenities?: string;
  /**
   * Curated catalog amenities attached to this accommodation via the
   * `accommodation_amenities` M2M relationship — projected alongside the
   * legacy `amenities` JSON-string-of-names field so icon-aware surfaces
   * can render without re-parsing free text. Empty for NextPax listings and
   * for native listings with no catalog amenities picked yet. Mirrors
   * `backend/schemas/accommodation.py::AmenityDetail`.
   */
  amenity_details?: AmenityDetail[];
  images?: PropertyImage[];
  primary_image_url?: string;
  is_available?: boolean;
  // Bookability discriminator — true means the listing has no fixed
  // chargeable price and is discoverable "by request" only. Mirrors
  // backend/models/accommodation.py::Accommodation.is_by_request.
  is_by_request?: boolean;
  // Admin-typed, display-only price (integer cents) shown to guests as
  // "From $X" on a top-level by-request listing — never read by pricing,
  // quote, cart, booking, or charge logic, and never part of sort or the
  // cheapest-child MIN. Only present when `is_by_request` is true and this
  // is not a child row; `null`/absent keeps the existing "By Request" /
  // "Price on request" rendering. Mirrors
  // backend/models/accommodation.py::Accommodation.by_request_from_price_cents.
  by_request_from_price_cents?: number | null;
  average_rating?: number;
  review_count?: number;
  created_at?: string;
  updated_at?: string;

  // Enhanced location and contact details
  neighborhood?: string;
  nearby_attractions?: string; // JSON string of nearby attractions
  approximate_location?: ApproximateLocation | null;

  // Detailed amenities structure
  detailed_amenities?: string; // JSON string with categorized amenities

  years_hosting?: number;

  // Brand attribution — derived from the listing's linked vendor/brand row;
  // all three are null when the listing has no linked brand.
  brand_name?: string;
  brand_logo_url?: string | null;
  brand_link?: string | null;

  // Check-in/out and policies
  check_in_time?: string;
  check_out_time?: string;
  house_rules?: string;

  // Admin-authored cancellation policy (top-level NATIVE listings only) and
  // the resolved policy (listing, then brand, then Terms default) returned on
  // detail reads.
  cancellation_policy_summary?: string | null;
  cancellation_policy_body?: string | null;
  native_cancellation_policy?: NativeCancellationPolicy | null;

  // Provider routing — NATIVE for Belhop-managed inventory, NEXTPAX for
  // channel-managed listings whose rates come from the engine via
  // /api/v1/accommodations/{id}/rate-breakdown.
  listing_source?: ListingSource;

  // Cheapest per-night rate (integer cents) projected from the latest
  // NextPax rate sync. Populated only for `listing_source === 'NEXTPAX'`;
  // `null` when the property has no eligible synced rows. Backend source:
  // backend/providers/nextpax/rate_sync.py::_compute_lowest_nightly_rate_cents.
  // Surfaced as the "From $X/night" preview on listing cards.
  lowest_nightly_rate_cents?: number | null;

  // --- Typed NextPax JSONB surfaces (story 4.1.7 slices 1, 2, 4) -----
  // All optional and only populated for `listing_source === 'NEXTPAX'`.
  // Native listings return `null` for every field. Slice 5 consumes
  // `occupancy.max_adults`, `occupancy.additional_children`,
  // `accepts_inputs`, and the ISO `currency`; slices 6/7 use the rest.
  occupancy?: OccupancyConstraints | null;
  cancellation_policies?: CancellationPolicy[] | null;
  prepayment_policy?: PrepaymentPolicy | null;
  // PM-level policy gating fields populated by NextPax policy sync.
  // Drives the pets / children stepper visibility, hard caps, and the
  // booking-validator pre-flight error codes. `null` for native
  // listings and for NEXTPAX listings whose policy sync has not yet
  // populated the JSONB. Mirrors
  // `backend/schemas/nextpax_jsonb.py::BookingPolicy`.
  booking_policy?: BookingPolicy | null;
  // PM-level display-only policy fields surfaced on the property-
  // policy disclosure UI. Mirrors
  // `backend/schemas/nextpax_jsonb.py::PropertyPolicyDisclosure`. `null`
  // for native listings and for NEXTPAX listings whose policy sync has
  // not yet populated the JSONB. Each inner field is independently
  // nullable — the disclosure rows gate on each one to collapse silently
  // when the underlying value is absent.
  policy_disclosure?: PropertyPolicyDisclosure | null;
  curated_amenities?: CuratedAmenity[] | null;
  /** Conditional fee triggers — e.g. `["pets", "vehicles"]`. Empty list
   *  means "no conditional fees"; `null`/absent means "not a NextPax
   *  listing". Drives which steppers render in the booking card and
   *  accordion. Slice 5 surfaces adults/children; slice 6's accordion
   *  consumes the full list for the Extras section. */
  accepts_inputs?: string[] | null;
  /** ISO currency code on the response (e.g. `"USD"`). Slice 5 falls
   *  back to USD when absent — the rate-breakdown carries its own
   *  authoritative currency per-line. */
  currency?: string | null;
  /** Health of the typed JSONB surfaces above. Mirrors
   *  `backend/schemas/nextpax_jsonb.py::ExternalDataStatus`:
   *  - `"available"` — JSONB parsed; typed fields populated where present.
   *  - `"not_synced"` — JSONB absent/empty; typed fields null.
   *  - `"malformed"` — JSONB present but rejected by Pydantic; typed
   *    fields force-null and the detail page renders a degraded-data
   *    notice so the user is told something is wrong rather than seeing
   *    a silently-empty NextPax surface.
   *  Native listings return `null`. */
  external_data_status?: 'available' | 'not_synced' | 'malformed' | null;

  // Hierarchy fields (native listings only — see AccommodationChildSummary
  // below). parent_id is undefined/null for a standalone or parent
  // accommodation, and the parent's id for a variant child. children carries
  // only live (non-soft-removed) variants, ordered (display_order, id).
  // Mirrors backend/schemas/accommodation.py::Accommodation.children/parent_id.
  parent_id?: number | null;
  // The parent's name, derived server-side from the parent relationship.
  // undefined/null for a standalone or parent accommodation. Mirrors
  // backend/schemas/accommodation.py::Accommodation.parent_name.
  parent_name?: string | null;
  children?: AccommodationChildSummary[];
  // Per-room cards for the guest "Where you'll sleep" section (ADR-0083).
  // Absent/[] for a NextPax listing (never authored), and for a container
  // parent with live options (the options each carry their own rows on
  // AccommodationChildSummary.bedroom_details instead -- see
  // backend/schemas/accommodation.py::apply_container_derivation_from_
  // live_children). Mirrors backend/schemas/accommodation.py::
  // Accommodation.bedroom_details.
  bedroom_details?: BedroomDetail[];
  // option_count is populated by the unified search matview for parent
  // accommodations (where children are not embedded). When > 0, the card
  // shows an "N options" qualifier alongside a "From $X" price. Mirrors
  // the identically-purposed Service.option_count field above.
  option_count?: number | null;
}

/**
 * Accommodation as returned by the admin-only routes. Adds the private
 * address and property-manager fields the public `Accommodation` omits;
 * mirrors backend `AccommodationAdminResponse`. Guest pages must use
 * `short_address` / `location` instead.
 */
export interface AccommodationAdmin extends Accommodation {
  full_address?: string;
  manager_name?: string;
  manager_email?: string;
  manager_phone?: string;
}

// Summary of a child variant embedded in the parent's accommodation detail
// response. Mirrors backend schemas/accommodation.py::AccommodationChildSummary.
export interface AccommodationChildSummary {
  id: number;
  name: string;
  description?: string;
  base_price_per_night?: number | null;
  max_guests: number;
  is_available: boolean;
  is_by_request: boolean;
  display_order: number;
  images?: PropertyImage[];
  // Fee-inclusive per-night rate — base_price_per_night * (1 + platform fee
  // rate + vendor tax rate). null for a by-request child with no base price.
  final_price_per_night?: number | null;
  price_disclosure: PriceDisclosure;
  // This variant's own curated catalog amenities, projected from its
  // accommodation_amenities M2M. Mirrors the parent's `amenity_details`
  // shape so the detail page can swap the amenity list on variant select.
  // Empty for a variant with no catalog amenities.
  amenity_details?: AmenityDetail[];
  // This variant's own bedroom rows -- unlike the parent, an option
  // always carries its own rows with no container-derivation override.
  // Mirrors backend/schemas/accommodation.py::AccommodationChildSummary.
  // bedroom_details.
  bedroom_details?: BedroomDetail[];
}

// Request body for POST /api/v1/accommodations/{id}/variants (mirrors
// backend schemas/accommodation.py::AccommodationVariantCreate). Variant
// authoring is native-only — the route 422s when the parent's
// listing_source isn't NATIVE. `name`/`base_price_per_night`/`max_guests`/
// `is_by_request` are caller-only and never fall back to the parent;
// `description`/`amenity_ids` fall back to the parent's own value when
// omitted from the request. The admin authoring form always sends an
// explicit `amenity_ids` array (including `[]`) so that parent-fallback
// path never fires on an authored row — see accommodationDraft.ts's
// variant payload builder. `display_order` does not fall back to the
// parent — it is the caller's sort position among sibling variants.
//
// This same shape also PUTs an existing variant's rename/reprice via
// apiService.updateAccommodation (the route accepts either the full
// AccommodationUpdatePayload or this narrower shape, mirroring
// ServiceUpdatePayload | ServiceVariantCreate).
export interface AccommodationVariantCreate {
  name: string;
  base_price_per_night: number | null;
  max_guests: number;
  is_by_request: boolean;
  display_order: number;
  description?: string | null;
  amenity_ids?: number[] | null;
}

export interface OccupancyConstraints {
  min_occupancy?: number | null;
  max_adults?: number | null;
  additional_children?: number | null;
  max_pets?: number | null;
}

export interface CancellationPolicy {
  // Mirrors `backend/schemas/accommodation.py::CancellationPolicy` on the
  // wire. Backend serialises with `response_model_by_alias=False`
  // (`backend/api/v1/accommodations.py`), so the camelCase aliases
  // (`policyId`, `policyName`, etc.) are input-only — the response
  // payload carries the snake_case keys. Anchored by
  // `backend/tests/unit/test_accommodation_response_typed_nextpax_fields.py`.
  policy_id?: number | null;
  policy_supplier_id?: number | null;
  policy_name?: string | null;
  policy_description?: string | null;
  refundable?: boolean | null;
  is_default?: boolean | null;
}

export interface PrepaymentPolicy {
  // Mirrors `backend/schemas/accommodation.py::PrepaymentPolicy` on the
  // wire. NextPax sync does not yet write this JSONB key (forward-
  // declared in slice 1) — the shape is set so the frontend lines up
  // when the sync side starts populating it.
  policy_id?: number | null;
  policy_name?: string | null;
  policy_description?: string | null;
  prepayment_percentage?: number | null;
}

export interface CuratedAmenity {
  code: string;
  label?: string | null;
}

// Mirrors `backend/schemas/nextpax_jsonb.py::BookingPolicy`. Populated
// from the NextPax PM (or per-property override) policy bundle. Drives
// the booking-options pets and children steppers and the booking
// validator pre-flight at checkout.
//
// `pets_allowed` is a closed enum of NextPax's documented values:
//   - `"allowed"`        — stepper visible, hard cap applies.
//   - `"not_allowed"`    — stepper hidden even if a stale PETS fee
//                          is present.
//   - `"on_request"`     — stepper visible with an "On request" badge;
//                          the customer understands confirmation is
//                          not automatic.
//   - `"follow_property_max_pets"` — stepper visible, cap pulled from
//                          `occupancy.max_pets`.
export interface BookingPolicy {
  pets_allowed?: 'allowed' | 'not_allowed' | 'on_request' | 'follow_property_max_pets' | null;
  max_pets?: number | null;
  children_allowed?: boolean | null;
  children_limit_per_adult?: boolean | null;
  children_limit?: number | null;
  contact_number_required?: boolean | null;
}

// Mirrors `backend/schemas/nextpax_jsonb.py::PropertyPolicyDisclosure`.
// Complement to `BookingPolicy`: every field here is something the
// property-policy disclosure UI shows the guest before they book; none
// of it gates the booking flow. Populated by the same policy sync.
// Backend serialises with `response_model_by_alias=False`, so the
// snake_case keys below are the on-wire shape — the camelCase aliases
// (`additionalInfoPets`, `smokingAllowed`, etc.) are input-only on the
// Pydantic side.
//
// `parking` carries NextPax's nested per-type rules; the inner shape is
// still loosely typed because the sandbox does not yet expose a stable
// schema for it. The disclosure UI reads
// `parking.parking === 'free' | 'paid'` and the optional `spaces` /
// `additional_info` keys defensively.
export interface PropertyPolicyDisclosure {
  pets_additional_info?: string | null;
  smoking_allowed?: boolean | null;
  non_smoking_option?: boolean | null;
  parking?: PropertyPolicyParking | null;
  internet_cost?: 'free' | 'paid' | null;
  key_collection_method?: string | null;
  damage_deposit_requirement?: string | null;
  guest_documentation_required?: string | null;
  children_age?: number | null;
  infants_age?: number | null;
}

// Inner shape of `PropertyPolicyDisclosure.parking`. The sandbox publishes
// `{parking: 'free' | 'paid', spaces?: number, additional_info?: string}`
// but the backend keeps the field as `dict | None` for now; the frontend
// reads it defensively and tolerates missing keys.
export interface PropertyPolicyParking {
  parking?: 'free' | 'paid' | null;
  spaces?: number | null;
  additional_info?: string | null;
}

// --- Eligible rate plans (story 4.1.7 slice 3) -----------------------
// Mirrors backend/schemas/eligible_rate_plans.py::EligibleRatePlanResponse.
// `rateplan_id` is the NextPax-side numeric id (int on wire — confirmed
// by slice 3 trace). `id` is the local `RatePlan.id` primary key used to
// disambiguate the selected plan when storing UI state.

export interface EligibleRatePlan {
  id: number;
  rateplan_id: number;
  scope: 'PROPERTY_MANAGER' | 'PROPERTY';
  name: string | null;
  pricing_type: string;
  pricing_offset: number;
  cancellation_policy_id: number | null;
  cancellation_policy_summary: string | null;
  // Server-resolved free-text describing the prepayment policy that
  // governs the selected plan (e.g. "Full payment at booking", "30%
  // deposit, balance due 30 days before check-in"). `null` when the
  // backend cannot resolve a summary — callers render it independently
  // when present, falling back to the property's full policy disclosure
  // only when the cancellation summary is also null.
  prepayment_policy_summary: string | null;
}

// --- Rate breakdown (NextPax-only) -----------------------------------
// Mirrors backend/schemas/rate_breakdown.py. All monetary fields are
// integer cents; `currency` is the unit.

export interface NightlyLine {
  date: string;
  rent_cents: number;
  // Story 4.1.7 slice 2 — per-line strikethrough metadata. Populated only
  // when the row carries a discount; `null`/absent for undiscounted
  // nights. Cert R-PRICE-02.
  original_price_cents?: number | null;
  discount_amount_cents?: number | null;
}

export interface FeeLine {
  code: string;
  // Human-readable label sourced from NextPax `/constants/mapping-codes`
  // at content-sync time; render verbatim. The backend falls back to the
  // raw code if NextPax has no translation, so this field is always a
  // non-empty string.
  name: string;
  amount_cents: number;
  currency: string;
  // Derived server-side as `(applicability == OPTIONAL) AND
  // (trigger == UNCONDITIONAL)`. Drives the opt-in checkbox in the
  // rate-breakdown drawer. Describes the fee's TYPE (is this opt-in-able?),
  // not its STATE (is it currently selected?) — a selected fee migrates
  // to `mandatory_fees` but still carries `is_selectable=true`.
  is_selectable: boolean;
  // Human-readable cadence sourced from the backend `FeeMode`:
  // `"Per stay"` (PER_STAY), `"Per night"` (PER_NIGHT), `"Per day"`
  // (PER_DAY), or `null` (PERCENTAGE — no clean human label). Display-
  // only: the Extras accordion's opt-in stepper renders
  // `${price} · ${cadence_label}` (e.g. `"$30.00 · Per stay"`) when
  // populated, and collapses to price-only when `null`. NOT consulted
  // by any computation.
  cadence_label: string | null;
}

// Property-level extra-person fee surfaced from the resolved rate row.
// The NextPax booking card renders a helper line below the Children
// stepper (e.g. "$50.00 extra-guest fee applies above 2 guests total")
// when this field is populated; renders nothing when the backend
// serialises it as `null` (rate row has amount_cents == 0).
export interface ExtraPersonFee {
  amount_cents: number;
  base_occupancy: number;
  currency: string;
}

export interface TaxLine {
  code: string;
  name: string;
  amount_cents: number;
  percentage: number | null;
  amount_flat_cents: number | null;
  currency: string;
}

export interface RateBreakdown {
  display_price: number;
  original_price: number | null;
  discount_amount: number | null;

  rent_subtotal: number;
  // Fees bucketed by NextPax `chargeType` applicability (see backend
  // `providers/nextpax/charge_type_classification.py`):
  // - `mandatory_*`: always in `display_price` and in the stayPrice we
  //   send to NextPax at checkout.
  // - `optional_*`: guest opt-in (e.g. pets). Surfaced so the guest can
  //   see the cost; NOT in `display_price` until opt-in lands.
  // - `pay_at_location_*`: paid to the property on arrival (e.g.
  //   tourist tax). Surfaced; NOT in `display_price`.
  mandatory_fees_subtotal: number;
  optional_fees_subtotal: number;
  pay_at_location_subtotal: number;
  taxes_subtotal: number;

  currency: string;

  mandatory_fees: FeeLine[];
  optional_fees: FeeLine[];
  pay_at_location_fees: FeeLine[];
  taxes: TaxLine[];
  nightly_breakdown: NightlyLine[];

  // Populated when the resolved rate row carries a non-zero
  // `extra_person_fee_amount_cents`; `null` when the row carries 0
  // (no extra-person fee at this property).
  extra_person_fee: ExtraPersonFee | null;

  check_in: string;
  check_out: string;
  nights: number;
  adults: number;
  children: number;
}

// --- Priced modifiers + quote (native-only) --------------------------
// Mirrors backend/schemas/modifier.py (catalogue) and
// backend/schemas/price_quote.py (quote). All monetary fields are integer
// cents; `currency` is the unit.

// Mirrors backend/models/modifier.py::ModifierPriceMode.
export type ModifierPriceMode = 'FLAT' | 'PER_UNIT' | 'PER_GUEST';

// Mirrors backend/schemas/modifier.py::ModifierResponse. `display_price_cents`
// is server-computed (single-unit inclusive price, tax + platform fee
// applied) — never derive it client-side from price_delta_cents.
export interface Modifier {
  id: number;
  code: string;
  name: string;
  description: string | null;
  price_delta_cents: number;
  display_price_cents: number;
  price_mode: ModifierPriceMode;
  max_quantity: number;
  is_default_selected: boolean;
  is_by_request: boolean;
  display_order: number;
}

// Mirrors backend/schemas/modifier.py::ModifierGroupResponse. `modifiers`
// is pre-ordered by (display_order, id) server-side.
export interface ModifierGroup {
  id: number;
  name: string;
  description: string | null;
  min_selections: number;
  max_selections: number | null;
  display_order: number;
  modifiers: Modifier[];
}

// Mirrors backend/schemas/booking.py::BookingModifierLine — a verbatim,
// read-only projection of a persisted `booking_modifiers` receipt row.
// Populated only on the guest and admin booking-detail (GET-by-id)
// responses, never on list responses (N+1 guard) — see `Booking.modifiers`
// below. `computed_amount_cents` is the server-computed, already-final
// amount for this line; the client divides by 100 to format and never
// recomputes it. Rows arrive pre-ordered by `display_order` (id
// tie-break) — do not re-sort client-side.
export interface BookingModifierLine {
  id: number;
  modifier_code: string;
  modifier_label: string;
  group_name: string;
  price_mode: ModifierPriceMode;
  quantity: number;
  computed_amount_cents: number;
  currency: string;
  display_order: number;
}

// --- Priced modifiers: admin authoring surface ------------------------
// Mirrors backend/schemas/modifier.py's admin-authoring schemas. Unlike
// the guest-facing pair above, these include inactive rows and expose the
// raw `price_delta_cents` with no display-price uplift — the admin is
// entering the catalogue price, not viewing a quote.

// Mirrors backend/schemas/modifier.py::ModifierAdminResponse.
export interface ModifierAdmin {
  id: number;
  code: string;
  name: string;
  description: string | null;
  price_delta_cents: number;
  price_mode: ModifierPriceMode;
  max_quantity: number;
  is_default_selected: boolean;
  is_by_request: boolean;
  display_order: number;
  is_active: boolean;
}

// Mirrors backend/schemas/modifier.py::ModifierGroupAdminResponse. Served
// by GET /{accommodations,services}/{id}/modifier-groups/admin.
// `attached_variant_id` is `null` when the group is attached to the
// edited listing itself (whole-listing scope), or the child variant's id
// when attached to one of its variants instead.
export interface ModifierGroupAdmin {
  id: number;
  name: string;
  description: string | null;
  min_selections: number;
  max_selections: number | null;
  display_order: number;
  is_active: boolean;
  attached_variant_id: number | null;
  modifiers: ModifierAdmin[];
}

// Request body for POST /{accommodations,services}/{id}/modifier-groups.
// Mirrors backend/schemas/modifier.py::ModifierGroupCreate.
// `attach_to_variant_id` selects the leg the created group attaches to:
// `null`/omitted attaches to the listing named by the path (whole-listing
// scope); an id attaches to that child variant instead. Attachment is
// fixed at create — ModifierGroupUpdatePayload carries no such field.
export interface ModifierGroupCreatePayload {
  name: string;
  description?: string | null;
  min_selections?: number;
  max_selections?: number | null;
  display_order?: number;
  attach_to_variant_id?: number | null;
}

// Request body for PUT /{accommodations,services}/{id}/modifier-groups/{gid}.
// Mirrors backend/schemas/modifier.py::ModifierGroupUpdate. Every field is
// optional — an omitted key means "leave unchanged" under the backend's
// partial-update semantics. Carries no attachment field: the leg a group
// attaches to is fixed at create and never re-assigned.
export interface ModifierGroupUpdatePayload {
  name?: string;
  description?: string | null;
  min_selections?: number;
  max_selections?: number | null;
  display_order?: number;
  is_active?: boolean;
}

// Request body for POST /{accommodations,services}/{id}/modifier-groups/{gid}/modifiers.
// Mirrors backend/schemas/modifier.py::ModifierCreate. Carries no `code`
// — the server derives it as a slug of `name` at create time.
// `is_by_request` is optional (server default `false`); when true the
// server rejects a nonzero `price_delta_cents`, so the mapper building
// this payload always sends `0` alongside `is_by_request: true`.
export interface ModifierCreatePayload {
  name: string;
  description?: string | null;
  price_delta_cents: number;
  price_mode: ModifierPriceMode;
  max_quantity?: number;
  is_default_selected?: boolean;
  is_by_request?: boolean;
  display_order?: number;
}

// Request body for PUT /{accommodations,services}/{id}/modifier-groups/{gid}/modifiers/{mid}.
// Mirrors backend/schemas/modifier.py::ModifierUpdate. Every field is
// optional — an omitted key means "leave unchanged" under the backend's
// partial-update semantics. Carries no `code` (immutable after create).
// `is_by_request` flips the flag; the server rejects a merged state where
// it is true with a nonzero `price_delta_cents`, so the mapper sends `0`
// alongside `is_by_request: true`.
export interface ModifierUpdatePayload {
  name?: string;
  description?: string | null;
  price_delta_cents?: number;
  price_mode?: ModifierPriceMode;
  max_quantity?: number;
  is_default_selected?: boolean;
  is_by_request?: boolean;
  display_order?: number;
  is_active?: boolean;
}

// Mirrors backend/schemas/price_quote.py::SelectedModifier. One modifier
// selection: which catalogue row, how many. `quantity` counts how many of
// this modifier were selected, never a per-request occurrence count by
// itself — the quote/checkout-cart wire expands it to occurrences.
export interface SelectedModifier {
  modifier_id: number;
  quantity: number;
}

// A DISPLAY-ONLY snapshot of a selected modifier's guest-facing label,
// quantity, and by-request flag, resolved once (at add-to-cart time) from
// the loaded `ModifierGroup[]` and persisted alongside the cart item so a
// cart row can name its extras without re-fetching the catalogue. Never
// sent on the checkout wire — `toNativeCheckoutLine` sends only the
// `SelectedModifier[]` pair above; a cart-rendering surface reads this
// snapshot instead.
export interface CartItemExtra {
  modifier_id: number;
  label: string;
  quantity: number;
  is_by_request: boolean;
}

// Mirrors backend/schemas/price_quote.py::QuoteLineKind.
export type QuoteLineKind = 'BASE' | 'MODIFIER';

// Mirrors backend/schemas/price_quote.py::QuoteLine. `group_name` and
// `price_mode` are null on the single BASE line and populated on every
// MODIFIER line.
export interface QuoteLine {
  kind: QuoteLineKind;
  code: string;
  label: string;
  group_name: string | null;
  price_mode: string | null;
  unit_amount_cents: number;
  quantity: number;
  unit_multiplier: number;
  tax_percentage_applied: number;
  platform_fee_percentage_applied: number;
  amount_cents: number;
}

// Mirrors backend/schemas/price_quote.py::PriceQuote — the server-
// authoritative, reconciled price quote for a listing + modifier
// selection. `parent_listing_name` is populated for a child variant/tier
// and null for a standalone or parent listing.
export interface PriceQuote {
  listing_type: BookingRequestKind;
  listing_id: number;
  listing_name: string;
  parent_listing_name: string | null;
  pricing_unit: string;
  unit_count: number;
  currency: string;
  lines: QuoteLine[];
  total_cents: number;
}

// --- Availability calendar (NextPax-only) ----------------------------
// Mirrors backend/schemas/availability_calendar.py. `available` collapses
// the wire-side per-property quantity to a boolean. The four restriction
// fields are `null` whenever NextPax doesn't opine on a given date —
// callers MUST treat `null` as "no restriction in that direction" (same
// convention as the booking engine).

export interface AvailabilityCalendarDate {
  date: string;
  available: boolean;
  arrival_allowed: boolean | null;
  departure_allowed: boolean | null;
  min_stay: number | null;
  max_stay: number | null;
}

export interface AvailabilityCalendar {
  accommodation_id: number;
  from_date: string;
  to_date: string;
  dates: AvailabilityCalendarDate[];
}

// Mirrors `backend/schemas/accommodation.py::AccommodationBase` /
// `AccommodationCreate` — only the fields the create path actually persists
// (see `backend/services/accommodation_service.py`'s explicit field
// disposition). Provider/ownership fields (vendor_id, listing_source,
// external_id, booking_flow_type, parent_id, manager_*, display_price,
// currency, last_synced_at) are deliberately absent: the backend derives or
// forces them server-side and never trusts a payload value for any of them.
// Required/optional here matches the backend schema's required/defaulted
// fields exactly — `type` has a backend default ("Villa") so it's optional.
export interface AccommodationCreate {
  name: string;
  description?: string;
  location: string;
  short_address?: string;
  full_address?: string;
  neighborhood?: string;
  // Plain string on create (unlike the update path, `AccommodationCreate`
  // has no list-to-JSON-string validator for this field).
  nearby_attractions?: string;
  type?: string;
  // Null only when `is_by_request` is true — mirrors the backend's create-side
  // pair validator (`AccommodationCreate.check_price_required_unless_by_request`),
  // which 422s a (null price, flag false) combination before it ever reaches
  // the DB CHECK.
  base_price_per_night: number | null;
  max_guests: number;
  bedrooms?: number;
  // Native-only bed count, independent of `bedrooms`. Omitted keeps the
  // backend's default (unset) on create.
  beds?: number;
  bathrooms?: number;
  square_feet?: number;
  // Catalog amenity ids picked via the admin `AmenityPicker` — replaces the
  // retired free-text `amenities` field. Sent as a plain array (possibly
  // empty) rather than left undefined; the backend writes the M2M rows and
  // derives the `amenities_text` names cache from them. An unknown id 422s.
  amenity_ids: number[];
  check_in_time?: string;
  check_out_time?: string;
  house_rules?: string;
  cancellation_policy_summary?: string;
  cancellation_policy_body?: string;
  // Belhop's markup on the vendor price, 0-100 with at most 2 decimals.
  platform_markup_percentage: number;
  vendor_tax_percentage?: number;
  // Attribution brand — mirrors backend schemas/accommodation.py::AccommodationCreate.
  // Omitted or undefined creates an ownerless (unbranded) listing.
  vendor_id?: number;
  // Bookability discriminator — see Accommodation.is_by_request above. Omit
  // (or send false) for a normally-priced listing; the backend defaults to
  // false when absent.
  is_by_request?: boolean;
  // Same display-only from-price described on
  // Accommodation.by_request_from_price_cents above. Omitted or `null` for
  // a normally-priced or priceless listing; the backend 422s a non-null
  // value sent without `is_by_request: true`.
  by_request_from_price_cents?: number | null;
}

// Admin request body for updating an accommodation via the accommodation
// edit form (mirrors backend/schemas/accommodation.py::AccommodationUpdate,
// scoped to the fields the edit form actually authors). Unlike
// `AccommodationCreate`, every field here is sent explicitly on every save:
// the backend's partial-PUT semantics treat an omitted key as "leave
// unchanged" and an explicit `null` as "clear" (verified against
// AccommodationUpdate + PUT /accommodations/{id}), so making every field
// required at the type level is deliberate — it makes a silently-dropped
// key a compile error, not just a runtime bug. `amenity_ids` and
// `nearby_attractions` (when its record uses the list shape, see
// accommodationDraft.ts) are list-typed fields — clearing either sends
// `[]`, not `null`, mirroring the backend's list-to-JSON-string coercion
// for `nearby_attractions` and the M2M-replace semantics for amenities.
// `is_available`, `listing_source`, `parent_id`, and the manager_* fields
// are deliberately absent: this form never authors them, and the partial
// PUT must never touch them.
export interface AccommodationUpdatePayload {
  name: string;
  description: string | null;
  location: string;
  short_address: string | null;
  full_address: string | null;
  neighborhood: string | null;
  // A JSON array of strings when the loaded record used the list shape, or
  // the raw trimmed text otherwise — see accommodationDraft.ts's
  // nearby_attractions round-trip contract.
  nearby_attractions: string[] | string | null;
  type: string;
  // Null only when `is_by_request` is true — mirrors the backend's
  // create-side pair validator (see AccommodationCreate.is_by_request above).
  base_price_per_night: number | null;
  // Deliberate exception to this interface's "every field sent explicitly"
  // doctrine documented above: a container listing's own Max Guests input is
  // hidden in the admin form (capacity is derived from its variants), so
  // `buildAccommodationUpdatePayload` omits this key entirely in
  // multiple-variants mode rather than sending a stale value — the
  // partial-PUT's exclude_unset semantics then leave the backend-derived
  // value untouched. A single-unit draft still sends it, unchanged.
  max_guests?: number;
  bedrooms: number | null;
  // Native-only bed count, independent of `bedrooms`. `null` clears a
  // previously-authored value.
  beds: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  // Catalog amenity ids picked via the admin `AmenityPicker` — replaces the
  // retired free-text `amenities` field. Always sent (possibly `[]`), which
  // replaces the accommodation's amenity set. See `AccommodationCreate.amenity_ids`.
  amenity_ids: number[];
  check_in_time: string | null;
  check_out_time: string | null;
  house_rules: string | null;
  cancellation_policy_summary: string | null;
  cancellation_policy_body: string | null;
  // Omitted to leave the stored markup unchanged (the API rejects null), and
  // on a NEXTPAX listing, which never authors one.
  platform_markup_percentage?: number;
  vendor_tax_percentage: number | null;
  vendor_id: number | null;
  // Bookability discriminator — see Accommodation.is_by_request above. Sent
  // explicitly on every save (required, not optional) so the partial-PUT
  // semantics documented on this interface apply here too: an omitted key
  // means "leave unchanged", so this can never be silently dropped.
  is_by_request: boolean;
  // Same display-only from-price described on
  // Accommodation.by_request_from_price_cents above. Sent explicitly on
  // every save (required, not optional) per this interface's partial-PUT
  // contract — an admin clearing the value, switching the toggle off, or
  // switching to multiple-variants mode sends explicit `null`, never an
  // omitted key.
  by_request_from_price_cents: number | null;
  // Admin-controlled sort position among sibling variants. Optional — the
  // parent's own form never authors this field; a variant update instead
  // sends this via the narrower AccommodationVariantCreate shape (see
  // apiService.updateAccommodation's widened parameter type). Mirrors
  // backend/schemas/accommodation.py::AccommodationUpdate.display_order.
  display_order?: number;
}

// Enhanced Booking interface
export interface Booking {
  id: number;
  user_id: number;
  trip_id?: number;
  accommodation_id?: number;
  service_id?: number;
  package_id?: number;
  // `null` on an undated booking (scheduling_mode UNDATED): no date or time at all.
  start_date: string | null;
  end_date: string | null;
  service_date?: string | null;
  service_time?: string | null;
  // Set only on a fixed-schedule booking whose occurrence has an end time.
  service_end_time?: string | null;
  scheduling_mode?: SchedulingMode;
  guest_count: number;
  // The backend always sends this; absent reads as true. False only for a
  // service booking whose guest page did not ask for a guest count.
  guest_count_applies?: boolean;
  // Granular guest split surfaced on the booking response so the
  // customer-facing detail and confirmation pages can render
  // "3 adults, 1 child" rather than collapsing to "4 guests".
  // Mirrors `backend/schemas/booking.py::Booking.adults` / `.children`;
  // `adults` is null for legacy rows persisted before these columns
  // existed — the display helper falls back to `guest_count` on the
  // null path. `children` defaults to `0`.
  adults?: number | null;
  children?: number;
  special_requests?: string;
  total_price: number;
  status:
    | 'PENDING'
    | 'AWAITING_PAYMENT'
    | 'CONFIRMED'
    | 'PENDING_CANCELLATION_REVIEW'
    | 'CANCELLED'
    | 'COMPLETED';
  payment_status: string;
  stripe_payment_intent_id?: string;
  // Stripe Checkout session URL + id for the AWAITING_PAYMENT
  // resolution flow. Populated when the admin approves a native
  // booking and the backend creates a Stripe Checkout Session;
  // ``null`` for every other status. Mirrors
  // ``backend/schemas/booking.py::Booking.checkout_url``.
  checkout_url?: string | null;
  stripe_checkout_session_id?: string | null;
  is_cancelled_by_guest: boolean;
  // Why the booking was actually cancelled, written by whoever cancelled it —
  // in practice the admin. Distinct from `guest_cancellation_reason` below:
  // before the two were split, an admin's cancellation overwrote the guest's
  // own words in this one column.
  cancellation_reason?: string;
  // The guest's own words when they requested cancellation, mirroring
  // `backend/schemas/booking.py::Booking.guest_cancellation_reason`. `null`
  // for bookings the guest never asked to cancel, and for rows that predate
  // the column — those are deliberately not backfilled, because the old
  // shared column cannot be attributed to a party without guessing.
  guest_cancellation_reason?: string | null;
  created_at: string;
  updated_at: string;
  // Provider/aggregator fields. Mirrors backend/schemas/booking.py::Booking
  // (~ line 88). `external_booking_ref` carries the supplier-side booking
  // reference (e.g. NextPax `bookingNumber`) support uses to contact the
  // property manager directly. Only the admin booking modal renders it, for
  // NEXTPAX bookings; guest surfaces show only `formatBookingReference`'s
  // Belhop id.
  // `booking_source` mirrors `backend/models/enums.py::ListingSource` and
  // drives provider routing (`providers/native.py` / `providers/nextpax/`)
  // and the frontend's choice of cancel flow, not any guest-visible label.
  external_booking_ref?: string | null;
  booking_source?: ListingSource | null;
  // The cancellation policy display text captured when the booking was created;
  // `null` on rows that predate the snapshot.
  cancellation_policy_snapshot?: CancellationPolicySnapshot | null;
  // Per-attempt UUID that ties this Booking to its originating
  // BookingDraft, its Stripe PaymentIntent metadata, and NextPax's
  // supplier record. Ops greps it across Stripe, NextPax and our logs;
  // only the admin booking modal renders it. `null` on legacy native
  // bookings that pre-date the draft flow.
  channel_partner_reference?: string | null;
  // Refund details persisted on the booking row by every refund-firing
  // route (customer cancel, admin pre-stay cancel, admin post-stay
  // cancel). Surfaced on the booking GET response so the customer
  // cancelled-hero on BookingDetailPage can render the "$X refunded
  // to Visa ending 4242" copy on any page load — not just from the
  // in-memory cancel response. All three stay null for bookings that
  // were never refunded. Mirrors `backend/schemas/booking.py::Booking`.
  refund_amount_cents?: number | null;
  refund_destination_last4?: string | null;
  refund_destination_brand?: string | null;
  // PM-level display-only policy disclosure (smoking / parking / internet /
  // key collection / damage deposit / documentation). Mirrors
  // `backend/schemas/booking.py::Booking.policy_disclosure` — populated by
  // the route handler from `booking.accommodation.policy_disclosure` so the
  // confirmation page can render the key-collection method and a deposit
  // reminder without a second `/accommodations/{id}` fetch. `null` for
  // native bookings and for NextPax bookings whose accommodation policy
  // sync hasn't populated the disclosure JSONB yet.
  policy_disclosure?: PropertyPolicyDisclosure | null;
  // ISO 4217 currency code for the booking amount (e.g. 'USD', 'EUR').
  // Drives the currency symbol on money-formatting surfaces such as
  // the approve-charge page. Defaults to 'USD' when absent.
  currency?: string;
  // Off-session payment state discriminator — drives the recovery action
  // surfaced on the booking-detail page. Computed server-side from the
  // booking row plus the latest charge_attempts row; never stored.
  //   'HARD_DECLINE_PARKED'       → Retry-payment action
  //   'SCA_RECOVERY_PARKED'       → Verify-payment action (3DS on-session)
  //   'REPRICE_APPROVAL_PENDING'  → Approve-reprice action
  // Mirrors `backend/schemas/booking.py::Booking.off_session_state`.
  off_session_state?: string | null;
  // Resolved charge amount in integer cents — may differ from `total_price`
  // after an admin reprice. Mirrors `backend/schemas/booking.py::Booking.final_charge_cents`.
  final_charge_cents?: number | null;
  // Decline code from the latest charge attempt. Populated when the latest
  // attempt has a non-null decline_code; null otherwise.
  // Mirrors `backend/schemas/booking.py::Booking.decline_code`.
  decline_code?: string | null;
  // The pre-reprice quote in cents, stamped onto the booking row the first
  // time it is repriced; null for a booking that has never been repriced.
  // Surfaced to the guest because `total_price` is the amount IN FORCE
  // (post-reprice) — any surface showing what actually changed needs this
  // as a separate field. Deriving the "original" from `total_price` renders
  // the in-force figure twice.
  // Mirrors `backend/schemas/booking.py::Booking.original_quote_cents`.
  original_quote_cents?: number | null;
  // True when the guest has an outstanding, unsatisfied reprice approval
  // request. Derived purely from booking columns (never from the latest
  // charge_attempts row) so this is safe on both the guest booking LIST
  // response and the detail response. Drives the awaiting-your-approval
  // qualifier on the booking detail page and the My Bookings list badge —
  // without it, a guest whose booking was repriced upward sees only the
  // new (higher) total_price with no signal that they haven't approved it
  // yet. Mirrors `backend/schemas/booking.py::Booking.reprice_approval_outstanding`.
  reprice_approval_outstanding?: boolean;
  // Cents the guest still owes for a delivered-metered booking, on the
  // detail response only; `null` on the list response and whenever nothing
  // is collectable (resolved, live charge claim, nothing due, not metered).
  // Mirrors `backend/schemas/booking.py::Booking.amount_due_cents`.
  amount_due_cents?: number | null;
  // Cents already captured on a delivered-metered booking, stamped by the
  // detail endpoint only. Mirrors `backend/schemas/booking.py::Booking.amount_paid_cents`.
  amount_paid_cents?: number | null;
  // The guest's next step on a delivered-metered booking, derived by the server from the
  // charge ledger: PAY (something is collectible) or APPROVE (the cap-leg is captured and the
  // excess awaits approval). Null when there is nothing to do. Guest detail endpoint only.
  // Mirrors `backend/schemas/booking.py::Booking.metered_balance_step`.
  metered_balance_step?: 'PAY' | 'APPROVE' | null;
  // Concierge-verified delivered units, written only when actuals are
  // finalised. Mirrors `backend/schemas/booking.py::Booking.actual_unit_count`.
  actual_unit_count?: number | null;
  // Units the guest estimated at checkout. Mirrors
  // `backend/schemas/booking.py::Booking.estimated_unit_count`.
  estimated_unit_count?: number | null;
  // Cap on the metered charge, in cents. Present only for a metered booking.
  // Mirrors `backend/schemas/booking.py::Booking.charge_cap_cents`.
  charge_cap_cents?: number | null;
  // Unit label snapshotted at booking time (e.g. 'hour'). Mirrors
  // `backend/schemas/booking.py::Booking.pricing_unit_snapshot`.
  pricing_unit_snapshot?: string | null;
  // Itemised receipt rows for this booking's priced modifiers, projected
  // verbatim from the persisted `booking_modifiers` table. Populated
  // (possibly `[]`) on the guest and admin GET-by-id detail responses
  // only; `null`/absent on list responses and for NextPax/package/legacy
  // bookings with no modifier rows. Mirrors
  // `backend/schemas/booking.py::Booking.modifiers`.
  modifiers?: BookingModifierLine[] | null;
  // Non-priced narration of the requested add-ons a converted
  // BookingRequest carried, stamped onto the row by the conversion
  // spawners; `null`/absent for every booking that did not spawn from a
  // converted request. Mirrors
  // `backend/schemas/booking.py::Booking.requested_modifiers_narration`.
  requested_modifiers_narration?: string | null;
}

// Mirrors backend/schemas/booking.py::DeltaPaymentIntentResponse.
// Returned by POST /api/v1/bookings/{id}/delta-payment-intent.
export interface DeltaPaymentIntentResponse {
  booking_id: number;
  payment_intent_id: string;
  client_secret: string;
  amount_cents: number;
  currency: string;
  saved_payment_method_id: string | null;
}

// Mirrors backend/schemas/booking.py::RecoveryIntentResponse.
// Returned by GET /api/v1/bookings/{id}/recovery-intent.
export interface RecoveryIntentResponse {
  booking_id: number;
  client_secret: string | null;
  intent_status: string;
  payment_method_id: string | null;
}

// Mirrors backend/schemas/booking.py::SettleRecoveryResponse.
// Returned by POST /api/v1/bookings/{id}/settle-recovery.
export interface SettleRecoveryResponse {
  booking_id: number;
  booking_status: string;
  payment_status: string;
  intent_status: string;
}

// Mirrors backend/schemas/booking.py::RetryPaymentResponse.
// Returned by POST /api/v1/bookings/{id}/retry-payment.
export interface RetryPaymentResponse {
  booking_id: number;
  charge_status: string;
  payment_intent_id: string | null;
  decline_code: string | null;
}

// Mirrors backend/schemas/booking.py::ApproveRepriceRequest.
// Sent by POST /api/v1/bookings/{id}/approve-reprice. `approved_amount_cents`
// must be the exact amount the customer was shown on the approval page —
// the server rejects a mismatch with 409 rather than charging silently.
export interface ApproveRepriceRequest {
  approved_amount_cents: number;
}

export interface ApproveRepriceResponse {
  message: string;
  booking_id: number;
  /**
   * Off-session charge outcome: 'succeeded', 'refused', 'failed',
   * 'requires_action', or 'ambiguous' when the booking was already
   * chargeable (AWAITING_PAYMENT) and the orchestrator fired inline.
   * 'consent_recorded' when the booking is still PENDING — the approval was
   * recorded but no charge was attempted; the charge fires later, without
   * asking again, once an admin approves the booking.
   * Mirrors `backend/schemas/booking.py::CONSENT_RECORDED_OUTCOME`.
   */
  charge_outcome: string | null;
  /** Stripe decline code on a hard-declined charge; null on success or when no charge attempted. */
  decline_code: string | null;
}

/**
 * Chauffeur-transfer direction. Mirrors TransferDirection in
 * backend/models/enums.py. Canonical home for this union — re-exported by
 * TransferDirectionToggle for its existing importers rather than defined
 * there, since the type describes wire/cart data, not the toggle component.
 */
export type TransferDirectionValue = 'to_hub' | 'from_hub';

// --- NextPax draft → confirm flow ------------------------------------
// Mirrors backend/schemas/booking.py::NextPaxBookingDraftCreate (~ line 187)
// and NextPaxBookingDraftResponse (~ line 222). The draft row carries the
// canonical channel_partner_reference UUID that links the Stripe
// PaymentIntent (authorised next) to the NextPax booking that ultimately
// confirms it.
//
// Guest mainBooker fields (10): guest_first_name, guest_last_name,
// guest_email, guest_street, guest_house_number, guest_zip_code,
// guest_place, guest_country_code, guest_date_of_birth (all required),
// guest_title_code (optional). These populate the NextPax ReservationMainBooker
// object on the supplier booking record; the backend gate (GUEST_FIELDS_INCOMPLETE)
// blocks draft creation when any required field is empty or whitespace.

export interface NextPaxBookingDraftCreate {
  accommodation_id: number;
  rate_plan_id: number | null;
  start_date: string;
  end_date: string;
  guest_count: number;
  pets: number;
  vehicles: number;
  extra_beds: number;
  // Opt-in OPT-UNCONDITIONAL fee codes the guest agreed to up front.
  // Selection-ordered; empty list when no opt-in fees were chosen.
  selected_fee_codes: string[];
  currency: string;
  total_price_cents: number;
  // Granular guest mix mirrored from the property card. The backend
  // pre-flight validator uses these to fire the child-related PM
  // policy gates (children-not-allowed and children-exceeds-adults)
  // before the draft persists. `adults` is optional/nullable so that
  // historical callers that don't supply it fall back to the
  // legacy "adults = max(0, guest_count - children)" derivation in
  // the backend schema default.
  children?: number;
  adults?: number | null;
  guest_info_id?: number | null;
  // Customer-typed contact + free-text note collected on the checkout
  // form. Optional/nullable for parity with the backend schema
  // (NextPaxBookingDraftCreate); the backend persists them on the draft
  // and plumbs them into the NextPax mainBooker phone + booking remarks.
  phone_number?: string | null;
  special_requests?: string | null;
  // Full guest mainBooker identity fields collected on the checkout form.
  // Required (9): the backend gate rejects a draft when any is empty/whitespace.
  // guest_date_of_birth must be YYYY-MM-DD (HTML date input emits this natively).
  // guest_country_code must be ISO 3166-1 alpha-2 (uppercase, validated by backend).
  guest_first_name: string;
  guest_last_name: string;
  guest_email: string;
  guest_street: string;
  guest_house_number: string;
  guest_zip_code: string;
  guest_place: string;
  guest_country_code: string;
  guest_date_of_birth: string;
  // Optional: one of "male" | "female" | "family"; omit or null when not supplied.
  guest_title_code?: string | null;
}

// Mirrors backend/schemas/booking.py::NextPaxBookingDraftGuestFieldsUpdate
// (and its shared base NextPaxMainBookerGuestFields, ~ line 700). Request
// body for PATCH /api/v1/bookings/draft-nextpax/{draft_id}. Carries EXACTLY
// the 12 NextPax mainBooker guest fields the create draft also carries — no
// price, date, occupancy, fee, or rate-plan field exists on this shape, so a
// body smuggling one of those keys cannot move it. Lets a guest correct
// their mainBooker details after a card has already been entered without
// recreating the underlying BookingDraft row, so draft_id and
// total_price_cents (and the already-authorised Stripe PaymentIntent) never
// change. Presence semantics mirror the guest fields on
// NextPaxBookingDraftCreate: the 9 non-title fields are required;
// guest_title_code, phone_number, and special_requests are optional/nullable.
export interface NextPaxDraftGuestFieldsUpdate {
  guest_first_name: string;
  guest_last_name: string;
  guest_email: string;
  guest_street: string;
  guest_house_number: string;
  guest_zip_code: string;
  guest_place: string;
  guest_country_code: string;
  guest_date_of_birth: string;
  guest_title_code?: string | null;
  phone_number?: string | null;
  special_requests?: string | null;
}

// Mirrors backend/schemas/booking.py::NextPaxBookingConfirmRequest
// (~ line 229). Request body for POST /api/v1/bookings/confirm-nextpax.
// The payment intent id is the Stripe authorisation that confirm-nextpax
// captures once the supplier accepts the booking.
export interface NextPaxBookingConfirmRequest {
  draft_id: number;
  payment_intent_id: string;
}

// Mirrors backend/schemas/booking.py::AccommodationBookingResponse
// (~ line 133). The shared envelope returned by both the legacy
// POST /api/v1/bookings/ endpoint and POST /api/v1/bookings/confirm-nextpax;
// callers consume the embedded booking row to navigate to
// /booking-confirmation/{booking.id}.
export interface AccommodationBookingResponse {
  booking: Booking;
  message: string;
  warning?: string | null;
}

export interface NextPaxBookingDraftResponse {
  draft_id: number;
  channel_partner_reference: string;
}

// Mirrors backend/schemas/payment.py::PaymentIntentResponse. Returned by
// POST /api/v1/payments/create-intent for the NextPax draft_id flow.
export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
  amount_cents: number;
  publishable_key: string;
  capture_method: string;
}

// Mirrors backend/models/booking_draft.py::BookingDraftStatus. The
// lifecycle of an in-flight NextPax booking draft. Transitions are
// one-way out of `PENDING_NEXTPAX`; the three terminal states
// (`CONFIRMED`, `FAILED`, `ORPHANED`) are mutually exclusive end
// points the frontend renders distinctly on the payment page so the
// guest can decide whether to view the confirmation, retry, or
// restart.
export type BookingDraftStatus = 'PENDING_NEXTPAX' | 'CONFIRMED' | 'FAILED' | 'ORPHANED';

// Mirrors backend/schemas/booking.py::CheckoutSessionVerifyRequest.
// Request body for POST /api/v1/bookings/{id}/verify-checkout-session.
// The customer-facing payment-success page forwards Stripe's
// success_url `session_id` query param on mount so the backend can
// idempotently flip AWAITING_PAYMENT -> CONFIRMED without depending
// on the Stripe webhook (which is unreachable in local dev).
export interface CheckoutSessionVerifyRequest {
  session_id: string;
}

// Mirrors backend/schemas/booking.py::CheckoutSessionVerifyResponse.
// `status` mirrors the BookingStatus enum literal after the flip;
// `booking_id` is the integer id so the success page can build the
// "View booking" CTA href without re-reading the URL :id param.
export interface CheckoutSessionVerifyResponse {
  status: 'PENDING' | 'AWAITING_PAYMENT' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  message: string;
  booking_id: number;
}

// Mirrors backend/schemas/booking.py::BookingDraftStateResponse.
// Read-only snapshot of an owned `BookingDraft` returned by GET
// /api/v1/bookings/draft-nextpax/{draft_id} for mid-flow refresh
// recovery on the NextPax payment page. `payment_intent_id` is an
// empty string before the first /create-intent round-trip and
// non-empty thereafter; `booking_id` is populated once the draft
// transitions to `CONFIRMED` so the page can navigate to
// /booking-confirmation/{booking_id} without a second round-trip.
export interface BookingDraftStateResponse {
  status: BookingDraftStatus;
  payment_intent_id: string;
  channel_partner_reference: string;
  accommodation_id: number;
  total_price_cents: number;
  currency: string;
  booking_id: number | null;
}

export interface BookingWithDetails extends Booking {
  user_name?: string;
  accommodation_name?: string;
  accommodation_location?: string;
  service_name?: string;
  service_location?: string;
  package_name?: string;
  can_cancel: boolean;
  can_modify: boolean;
}

export interface BookingAdmin extends BookingWithDetails {
  // Free-text admin scratchpad. The update route overwrites this wholesale,
  // which is why the audit trail below no longer shares it.
  admin_notes?: string;
  // Append-only record of which admin acted on the booking and why, mirroring
  // `backend/schemas/booking.py::BookingAdmin.audit_log`. Read-only on every
  // surface: no request body carries it, so the notes editor cannot retype or
  // delete it. `null` for rows that predate the column — deliberately not
  // backfilled.
  audit_log?: string | null;
  // Surfaced by the booking poller when a PM-initiated cancellation is
  // detected. Admins clear this flag via the admin update route once
  // the refund decision has been actioned. Default false.
  needs_admin_review: boolean;
  // Off-session charge eligibility fields, surfaced on the admin booking
  // response so the details modal can gate the charge trigger.
  // True when a Stripe saved-payment-method is on file.
  has_saved_payment_method?: boolean;
  // True when the booking is in a state where the admin can charge now
  // (charge_eligible = has card + not awaiting customer re-approval).
  charge_eligible?: boolean;
  // The admin-set charge amount in cents, if different from the listing price.
  // Null means no repricing has occurred — fall back to total_price.
  final_charge_cents?: number | null;
  // When a metered booking's actual usage was finalised; null until then.
  // Mirrors `backend/schemas/booking.py::BookingAdmin.actuals_finalized_at`.
  actuals_finalized_at?: string | null;
  // When an admin resolved an uncollected metered balance; null until then.
  // Mirrors `backend/schemas/booking.py::BookingAdmin.uncollected_resolved_at`.
  uncollected_resolved_at?: string | null;
  // Server-derived: a metered booking past its service date with no actuals
  // finalised yet. Mirrors `backend/schemas/booking.py::BookingAdmin.needs_actuals`.
  needs_actuals: boolean;
  // `original_quote_cents` and `reprice_approval_outstanding` are inherited
  // from the base `Booking` interface (shared with the guest-facing
  // surfaces). While `reprice_approval_outstanding` holds, the admin's
  // approve-and-charge control must not be offered — the server refuses to
  // charge until the guest approves.
}

// Mirrors backend/schemas/booking.py::FinalizeActualsResponse.
// Returned by POST /api/v1/admin/bookings/{id}/finalize-actuals.
export type FinalizeChargeOutcome =
  'succeeded' | 'failed' | 'requires_action' | 'ambiguous' | 'refused';

export interface FinalizeActualsResponse {
  message: string;
  booking_id: number;
  status: Booking['status'];
  final_charge_cents: number;
  over_cap: boolean;
  delta_due_cents: number;
  charge_outcome: FinalizeChargeOutcome | null;
  reapproval_requested: boolean;
}

// Mirrors backend/schemas/booking.py::ResolveUncollectedResponse.
// Returned by POST /api/v1/admin/bookings/{id}/resolve-uncollected.
export type UncollectedResolution = 'SETTLED_AT_CAP' | 'WRITTEN_OFF';

export interface ResolveUncollectedResponse {
  message: string;
  booking_id: number;
  status: Booking['status'];
  resolution: UncollectedResolution;
  final_charge_cents: number;
  captured_cents: number;
  shortfall_cents: number;
  uncollected_resolved_at: string | null;
}

// Mirrors backend/schemas/booking.py::RepriceRequest.
// Request body for POST /api/v1/admin/bookings/{id}/reprice.
export interface RepriceRequest {
  new_charge_cents: number;
  reason: string;
}

// Mirrors backend/schemas/booking.py::RepriceResponse.
// Returned by POST /api/v1/admin/bookings/{id}/reprice.
// reapproval_required=false: amount is ≤ listing price;
// the saved card is charged automatically on admin approval.
// reapproval_required=true: amount exceeds listing price;
// the customer must re-approve before the card is charged.
export interface RepriceResponse {
  message: string;
  booking_id: number;
  new_charge_cents: number;
  reapproval_required: boolean;
}

// Review System
export interface Review {
  id: number;
  rating: 1 | 2 | 3 | 4 | 5;
  comment?: string;
  // 'submitted' is a terminal outbox state for a provider-sourced
  // accommodation's guest review — accepted by the external
  // destination, awaiting the next sync to echo it back as published.
  status: 'pending' | 'approved' | 'rejected' | 'submitted';
  user_id: number;
  accommodation_id: number;
  booking_id: number;
  created_at: string;
  updated_at: string;
  user_full_name?: string;
}

export interface ReviewCreate {
  rating: 1 | 2 | 3 | 4 | 5;
  comment?: string;
  accommodation_id: number;
  booking_id: number;
}

// A published review synced from a provider's shared review pool for a
// provider-sourced accommodation. Has no local user or moderation state.
export interface ExternalReview {
  id: number;
  headline?: string | null;
  body?: string | null;
  positive?: string | null;
  negative?: string | null;
  normalised_rating?: number | null;
  language?: string | null;
  reviewer_country_code?: string | null;
  origin: string;
  arrival_date?: string | null;
  departure_date?: string | null;
  reviewed_at?: string | null;
  created_at: string;
  is_belhop_guest: boolean;
}

export interface ReviewStats {
  total_reviews: number;
  average_rating: number;
  rating_distribution: {
    '5': number;
    '4': number;
    '3': number;
    '2': number;
    '1': number;
  };
}

// Wishlist
export interface WishlistItem {
  id: number;
  user_id: number;
  accommodation_id?: number;
  service_id?: number;
  created_at: string;
  accommodation?: Accommodation;
  service?: Service;
}

// Property Images
export interface PropertyImage {
  id: number;
  url: string;
  alt_text?: string;
  is_primary: boolean;
  display_order: number;
  accommodation_id: number;
}

export interface ImageUploadResponse {
  url: string;
  filename: string;
  size: number;
}

export interface InclusionItem {
  label: string;
  icon: string | null;
}

export interface InclusionPresetWrite {
  label: string;
  icon: string;
}

export interface InclusionPreset extends InclusionPresetWrite {
  id: number;
  version: number;
  created_at: string;
  updated_at: string | null;
}

export interface InclusionPresetUpdate extends InclusionPresetWrite {
  expected_version: number;
}

export interface InclusionPresetQuery {
  search?: string;
  limit?: number;
  offset?: number;
}

// Summary of a child variant embedded in the parent's service detail response.
// Mirrors backend schemas/service.py ServiceChildSummary.
export interface ServiceChildSummary {
  id: number;
  name: string;
  price_disclosure: PriceDisclosure;
  price_per_person?: number;
  pricing_unit?: string;
  duration?: number;
  max_capacity?: number | null;
  is_available: boolean;
  // Per-option applicability flag the guest option list reads to decide
  // whether the SELECTED option needs a direction — never the listing as a
  // whole. Mirrors backend schemas/service.py::ServiceChildSummary
  // .is_hub_transfer (see backend/models/service.py::Service
  // .is_directional_option for the one rule this feeds). Defaults true
  // server-side; `?? true` at the read site here mirrors that default for
  // fixtures built before this field existed on the wire.
  is_hub_transfer?: boolean;
  description?: string;
  included?: InclusionItem[];
  final_price_per_person?: number;
  // Mirrors backend schemas/service.py:189-190 — the child's own ordered
  // image set and its display position among sibling children. Used to
  // swap the parent's gallery and to render the per-option thumbnail.
  images?: ServiceImage[];
  display_order?: number;
  // The option's own billing mechanism and unit-count bounds. Mirrors
  // backend schemas/service.py::ServiceChildSummary. Optional because
  // fixtures built before these fields existed omit them; absent reads as
  // FIXED_QUOTE with no bounds.
  billing_basis?: BillingBasis;
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
  // No fixed price for this option — quoted by an admin per customer.
  // Mirrors backend schemas/service.py::ServiceChildSummary.is_by_request.
  // Optional here (unlike AccommodationChildSummary's required equivalent)
  // because several already-typed guest-facing fixtures build this shape
  // without the field; a consumer that cares reads it defensively as falsy
  // when absent.
  is_by_request?: boolean;
}

/** The cart services offer moment a category feeds; null means Not offered. */
export type OfferMoment = 'arrive' | 'dine' | 'restore' | 'explore';

/** One entry of the public category list: active categories only, in admin order. */
export interface PublicServiceCategory {
  slug: string;
  label: string;
  badge_label: string | null;
  offer_moment: OfferMoment | null;
}

/** The category embedded in a service response; null on the service when it has none. */
export interface ServiceCategoryDetail extends PublicServiceCategory {
  is_active: boolean;
}

/** A category as the admin list returns it: every category, active or not, in display order. */
export interface AdminServiceCategory extends PublicServiceCategory {
  id: number;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

export interface ServiceCategoryCreate {
  label: string;
  slug: string;
  badge_label: string | null;
  offer_moment?: OfferMoment | null;
}

/** Partial update: the slug is immutable, so it is not part of this shape. */
export interface ServiceCategoryUpdate {
  label?: string;
  badge_label?: string | null;
  offer_moment?: OfferMoment | null;
  is_active?: boolean;
}

// Standalone Services
export interface Service {
  id: number;
  slug?: string;
  name: string;
  description: string;
  short_address?: string;
  service_type: 'dining' | 'transportation' | 'spa_wellness' | 'activities' | 'shopping' | 'events';
  // The category's label, badge and active flag; null when `category` is null.
  category_detail?: ServiceCategoryDetail | null;
  location: string;
  // Belhop's markup on the vendor price, serialised by the API as a decimal
  // string (for example "12.50").
  platform_markup_percentage: string;
  price_disclosure: PriceDisclosure;
  vendor_tax_percentage?: number; // Vendor's preferred tax percentage
  vendor_id?: number;
  final_price_per_person?: number; // Calculated final price with platform fee and taxes
  // Deprecated - use final_price_per_person instead
  price_per_person?: number;
  pricing_unit?: string; // Unit for pricing (e.g., "person", "hour", "30min")
  duration?: number; // in minutes
  max_capacity: number | null;
  min_capacity?: number;
  is_available: boolean;
  // Bookability discriminator — true means the service has no fixed
  // chargeable price and is discoverable "by request" only. Mirrors
  // backend/models/service.py::Service.is_by_request.
  is_by_request?: boolean;
  // Admin-set preference for the cart services offer: a highlighted service
  // is offered ahead of the default pick in its moment. Absent reads as not
  // highlighted. Mirrors backend/models/service.py::Service.is_offer_highlight.
  is_offer_highlight?: boolean;
  // Admin-typed, display-only price (integer cents) shown to guests as
  // "From $X" on a top-level by-request listing — never read by pricing,
  // quote, cart, booking, or charge logic, and never part of sort or the
  // cheapest-child MIN. Only present when `is_by_request` is true and this
  // is not a child row; `null`/absent keeps the existing "By Request" /
  // "Price on request" rendering. Mirrors
  // backend/models/service.py::Service.by_request_from_price_cents.
  by_request_from_price_cents?: number | null;
  vendor_contact?: string;
  included: InclusionItem[];
  images: ServiceImage[];
  average_rating?: number;
  review_count?: number;
  created_at?: string;
  updated_at?: string;
  google_place_id?: string;
  google_rating?: number;
  google_review_count?: number;
  google_review_link?: string;
  // Free-text pickup/drop-off hub name (e.g. "Aspen/Snowmass"). Parent-level
  // only — presence is the trigger for the guest-facing To/From direction
  // selector; null/absent means the service has no direction selector.
  // Mirrors backend/models/service.py::Service.transfer_hub_label.
  transfer_hub_label?: string | null;
  // Top-level services only; absent on older responses means GUEST_PICKS.
  scheduling_mode?: SchedulingMode;
  // Admin-authored cancellation policy (top-level NATIVE services only) and
  // the resolved policy returned on detail reads.
  cancellation_policy_summary?: string | null;
  cancellation_policy_body?: string | null;
  native_cancellation_policy?: NativeCancellationPolicy | null;
  // Per-option applicability flag for the hub-relative direction selector —
  // see ServiceChildSummary.is_hub_transfer above for the full rationale.
  // Read on this type only when a standalone (childless) service carries
  // its own hub. Mirrors backend/models/service.py::Service.is_hub_transfer.
  is_hub_transfer?: boolean;
  available_days?: string[]; // Array of weekday names like ["Monday", "Tuesday"]
  primary_image_url?: string;
  years_hosting?: number;

  // Brand attribution — derived from the service's linked vendor/brand row;
  // all three are null when the service has no linked brand.
  brand_name?: string;
  brand_logo_url?: string | null;
  brand_link?: string | null;
  // Hierarchy fields
  parent_id?: number | null;
  // The parent's name, derived server-side from the parent relationship.
  // undefined/null for a standalone or parent service. Mirrors
  // backend/schemas/service.py::ServiceInDB.parent_name.
  parent_name?: string | null;
  category?: string | null;
  children?: ServiceChildSummary[];
  // option_count is populated by the unified search matview for parent services
  // (where children are not embedded). When > 0, the card shows "N options" and
  // "From $X" without a unit suffix (the unified-row rule). Gap-driven extension:
  // the Service TS type previously lacked this field even though the backend
  // search/list endpoint returns it.
  option_count?: number | null;

  // Provider routing — NATIVE for Belhop-managed inventory, NEXTPAX for
  // channel-managed listings. Mirrors the Accommodation interface's
  // identically-named field above; drives the admin service-edit form's
  // client-side image-management gate (provider-sourced images are
  // sync-managed and must not be bulk-replaced from this form).
  listing_source?: ListingSource;

  // Billing mechanism discriminator — see BillingBasis above. Optional so
  // every pre-existing response payload that omits it (nothing populates
  // it yet on most surfaces) keeps parsing exactly as it did before this
  // field was added. Mirrors backend/models/service.py::Service.billing_basis.
  billing_basis?: BillingBasis;
  // Bounds on the bookable unit count, meaningful only once billing_basis
  // is METERED, but accepted regardless of basis. Mirrors
  // backend/models/service.py::Service.minimum_unit_count /
  // maximum_unit_count.
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
}

/**
 * Billing mechanism discriminator for a service. `FIXED_QUOTE` is a flat
 * price known before booking; `METERED` bills by the actual usage recorded
 * after the service completes, up to a dollar cap shown at checkout.
 *
 * Backend source of truth: backend/models/service.py::BillingBasis.
 */
export type BillingBasis = 'FIXED_QUOTE' | 'METERED';

export interface ServiceImage {
  id: number;
  url: string;
  alt_text: string;
  is_primary: boolean;
  display_order: number;
  service_id: number;
}

// A package as returned by GET /api/v1/packages (and the admin variants).
// price_display / price_original_display / price_savings_display are
// pre-formatted display copy, not amounts — a package with no price at all
// serialises all three as null rather than omitting them.
export interface Package {
  id: number;
  name: string;
  slug: string;
  tag: string | null;
  description: string | null;
  includes: string[];
  price_display: string | null;
  price_original_display: string | null;
  price_savings_display: string | null;
  badge: string | null;
  is_featured: boolean;
  display_order: number;
  is_active: boolean;
  images: PackageImage[];
  // Server-authoritative price in cents, and the two fields the booking
  // guard checks against — nullable because the source data can predate
  // them. Mirrors backend/schemas/package.py::PackageResponse.
  price_cents: number | null;
  duration_nights: number | null;
  max_guests: number | null;
  // Bookability discriminator superseding the earlier `price_cents !=
  // null` rule: true means the package has no fixed chargeable price —
  // PackagePurchasePanel renders the request-a-quote form instead of live
  // booking controls. Mirrors backend/models/package.py::Package.is_by_request.
  is_by_request: boolean;

  // Hierarchy fields (native listings only — see PackageChildSummary below).
  // parent_id is undefined/null for a standalone or parent package, and the
  // parent's id for a variant child. children carries only live
  // (non-soft-removed) variants, ordered (display_order, id). Mirrors
  // backend/schemas/package.py::PackageResponse.children/parent_id.
  parent_id?: number | null;
  // The parent's name, derived server-side from the parent relationship.
  // undefined/null for a standalone or parent package. Mirrors
  // backend/schemas/package.py::PackageResponse.parent_name.
  parent_name?: string | null;
  children?: PackageChildSummary[];
  // Cheapest live child price, falling back to this package's own
  // price_cents when it has no priced live children (including the
  // standalone case, where it always equals price_cents). Mirrors
  // backend/schemas/package.py::PackageResponse.from_price_cents.
  from_price_cents?: number | null;
}

// Summary of a child variant embedded in the parent's package detail
// response. Mirrors backend schemas/package.py::PackageChildSummary.
export interface PackageChildSummary {
  id: number;
  name: string;
  description?: string;
  price_cents: number | null;
  duration_nights: number | null;
  max_guests: number | null;
  includes: string[];
  is_by_request: boolean;
  is_active: boolean;
  display_order: number;
  images?: PackageImage[];
}

export interface PackageImage {
  id: number;
  package_id: number;
  url: string;
  alt_text: string | null;
  is_primary: boolean;
  display_order: number;
}

// Admin request body for creating a package (mirrors
// backend/schemas/package.py::PackageCreate). `slug` left undefined
// auto-generates one from `name`; an explicit slug that collides with an
// existing package is rejected with 409.
export interface PackageCreate {
  name: string;
  slug?: string | null;
  tag?: string | null;
  description?: string | null;
  includes?: string[];
  price_display?: string | null;
  price_original_display?: string | null;
  price_savings_display?: string | null;
  badge?: string | null;
  is_featured?: boolean;
  display_order?: number;
  is_active?: boolean;
  // Server-authoritative price in cents plus the booking-guard fields —
  // mirrors backend/schemas/package.py::PackageCreate. Nullable/omittable
  // because a package can be created display-only (by-request), with these
  // authored later once it becomes bookable.
  price_cents?: number | null;
  duration_nights?: number | null;
  max_guests?: number | null;
  // See Package above — mirrors backend/schemas/package.py::PackageCreate.
  // Defaults False server-side when omitted.
  is_by_request?: boolean;
}

// Admin request body for updating a package (mirrors
// backend/schemas/package.py::PackageUpdate). Every field is optional —
// feature/unfeature and activate/deactivate are plain single-field updates
// through this same shape, not separate endpoints.
export interface PackageUpdate {
  name?: string;
  slug?: string;
  tag?: string | null;
  description?: string | null;
  includes?: string[];
  price_display?: string | null;
  price_original_display?: string | null;
  price_savings_display?: string | null;
  badge?: string | null;
  is_featured?: boolean;
  display_order?: number;
  is_active?: boolean;
  // See PackageCreate above — same fields, mirrors
  // backend/schemas/package.py::PackageUpdate.
  price_cents?: number | null;
  duration_nights?: number | null;
  max_guests?: number | null;
  is_by_request?: boolean;
}

// Request body for POST /api/v1/packages/{id}/variants (mirrors backend
// schemas/package.py::PackageVariantCreate). `tag`/`badge` always come from
// the parent and cannot be overridden here; `slug`/`is_featured`/`parent_id`
// are set server-side and are never accepted in a request body.
// `description`/`duration_nights`/`max_guests` fall back to the parent's own
// value when omitted (or sent `null`); `name`/`price_cents`/`includes`/
// `is_by_request` are caller-only. `display_order` is the caller's sort
// position among sibling tiers and does not fall back to the parent.
export interface PackageVariantCreate {
  name: string;
  price_cents: number | null;
  includes: string[];
  is_by_request: boolean;
  display_order: number;
  description?: string | null;
  duration_nights?: number | null;
  max_guests?: number | null;
}

// Mirrors backend/models/booking_request.py::BookingRequestStatus.
// DECLINED / WITHDRAWN / CONVERTED are terminal; EXPIRED is re-openable by
// an admin re-quote.
export type BookingRequestStatus =
  'REQUESTED' | 'QUOTED' | 'CONVERTED' | 'DECLINED' | 'EXPIRED' | 'WITHDRAWN';

// A customer's persisted intent on a by-request listing, as returned by
// POST /api/v1/booking-requests (mirrors
// backend/schemas/booking_request.py::BookingRequestResponse). All three
// legs are populated; `listing_name` resolves for every leg. `end_date` is
// the accommodation leg's check-out date — null for the service and
// package legs, which carry only `start_date`.
export interface BookingRequest {
  id: number;
  user_id: number | null;
  accommodation_id: number | null;
  service_id: number | null;
  package_id: number | null;
  booking_id: number | null;
  // Null for an undated service request, which carries no date.
  start_date: string | null;
  end_date: string | null;
  // "HH:MM" times of a fixed-schedule request's occurrence; null when it has none.
  start_time?: string | null;
  end_time?: string | null;
  scheduling_mode?: SchedulingMode;
  guest_count: number;
  notes: string | null;
  status: BookingRequestStatus;
  quoted_total_cents: number | null;
  quoted_at: string | null;
  quote_expires_at: string | null;
  listing_name: string | null;
  // The owning QuoteGroup's id, or null when this request is ungrouped —
  // mirrors backend/schemas/booking_request.py::BookingRequestResponse's
  // own quote_group_id field, which always serialises (Pydantic default
  // None). Non-null while the request is a live or settled member of a
  // group quote; a grouped request is never individually approved,
  // verified, or withdrawn.
  quote_group_id: number | null;
  // The listing's display image URL, chosen server-side (a variant falls
  // back to its parent's image) — mirrors
  // backend/schemas/booking_request.py::BookingRequestResponse's own
  // image_url field, which always serialises. Null when neither the
  // listing nor its parent has an image.
  image_url: string | null;
}

// Request body for POST /api/v1/booking-requests (mirrors
// backend/schemas/booking_request.py::BookingRequestCreate). Polymorphic —
// exactly one of accommodation_id / service_id / package_id must be set,
// mirroring the backend's exactly-one-of-three model validator (itself a
// mirror of the DB CHECK). end_date is the check-out date, required when
// accommodation_id is set and forbidden otherwise (enforced server-side by
// the same validator).
export interface BookingRequestCreate {
  accommodation_id?: number;
  service_id?: number;
  package_id?: number;
  start_date: string;
  end_date?: string;
  guest_count: number;
  notes?: string | null;
  // Priced + by-request modifier selection carried into the request's
  // eventual quote. Mirrors `CartCheckoutLineRequest.selected_modifiers`'s
  // omit-when-empty convention — omitted (rather than sent empty) when the
  // request carries no modifier selection, so a caller built before this
  // field existed produces a byte-identical wire payload.
  selected_modifiers?: SelectedModifier[];
}

// The listing-leg discriminator on an admin booking-request row. Mirrors
// backend/models/booking.py::BookingType — reused there for the same
// "what kind of listing is this" purpose on a Booking.
export type BookingRequestKind = 'ACCOMMODATION' | 'SERVICE' | 'PACKAGE';

// One row of the admin Requests-tab list, and the response of the
// quote/decline actions on a single row (mirrors
// backend/schemas/booking_request.py::BookingRequestAdmin). `listing_name`
// resolves for all three legs. `end_date` is the accommodation leg's
// check-out date — null for the service and package legs.
export interface BookingRequestAdmin {
  id: number;
  kind: BookingRequestKind;
  listing_name: string | null;
  accommodation_id: number | null;
  service_id: number | null;
  package_id: number | null;
  customer_name: string | null;
  customer_email: string | null;
  // Null for an undated service request, which carries no date.
  start_date: string | null;
  end_date: string | null;
  // "HH:MM" local wall time from the chosen occurrence; null otherwise.
  start_time?: string | null;
  end_time?: string | null;
  scheduling_mode?: SchedulingMode;
  guest_count: number;
  notes: string | null;
  status: BookingRequestStatus;
  quoted_total_cents: number | null;
  quoted_at: string | null;
  quote_expires_at: string | null;
  // The owning QuoteGroup's id, or null when this request is ungrouped —
  // mirrors backend/schemas/booking_request.py::BookingRequestAdmin's own
  // quote_group_id field, which always serialises (Pydantic default
  // None). Non-null while the request is a live or settled member of a
  // group quote.
  quote_group_id: number | null;
}

// Admin request body for POST /admin/booking-requests/{id}/quote — the
// same body serves the initial quote, a revise, and a re-open (mirrors
// backend/schemas/booking_request.py::BookingRequestQuoteRequest).
export interface BookingRequestQuoteRequest {
  quoted_total_cents: number;
}

// Response body for POST /api/v1/booking-requests/{id}/approve — the
// on-session Stripe PaymentIntent minted (or idempotently returned) for
// exactly the currently-issued quote (mirrors
// backend/schemas/booking_request.py::BookingRequestApproveResponse).
export interface BookingRequestApproveResponse {
  client_secret: string;
  payment_intent_id: string;
  amount_cents: number;
  currency: string;
}

// Request body for POST /api/v1/booking-requests/{id}/verify-payment —
// the frontend forwards the PaymentIntent id it just confirmed with
// Stripe Elements (mirrors
// backend/schemas/booking_request.py::BookingRequestVerifyPaymentRequest).
export interface BookingRequestVerifyPaymentRequest {
  payment_intent_id: string;
}

// Response body for POST /api/v1/booking-requests/{id}/verify-payment
// (mirrors
// backend/schemas/booking_request.py::BookingRequestVerifyPaymentResponse).
// `booking_id` is present whenever `status` is CONVERTED.
export interface BookingRequestVerifyPaymentResponse {
  status: BookingRequestStatus;
  booking_id: number | null;
  message: string;
}

// Mirrors backend/models/quote_group.py::QuoteGroupStatus — a separate
// backend enum from BookingRequestStatus, but the same member set and the
// same terminal/re-openable shape: DECLINED / WITHDRAWN / CONVERTED are
// terminal, EXPIRED is re-openable by an admin re-quote.
export type QuoteGroupStatus =
  'REQUESTED' | 'QUOTED' | 'CONVERTED' | 'DECLINED' | 'EXPIRED' | 'WITHDRAWN';

// The persisted QuoteGroup row plus its members, as returned to the
// customer by every group read route in any status (mirrors
// backend/schemas/quote_group.py::QuoteGroupResponse). A post-quote-
// DECLINED member RETAINS its last quoted_total_cents (the backend
// deliberately does not null it out on decline) — only QUOTED members'
// amounts count toward the group's own total_cents (mirrors
// backend/services/quote_group.py::_recompute_quote_group_total_cents).
// Every other member's own status and quoted_total_cents describe it
// independently of the group's status.
export interface QuoteGroup {
  id: number;
  status: QuoteGroupStatus;
  total_cents: number | null;
  quoted_at: string | null;
  quote_expires_at: string | null;
  cart_checkout_id: number;
  // The group's cover photo, chosen server-side (the cart's stay when
  // there is one) — mirrors backend/schemas/quote_group.py::QuoteGroupResponse's
  // own cover_image_url field, which always serialises. Null when no
  // candidate has an image.
  cover_image_url: string | null;
  members: BookingRequest[];
}

// Response body for POST
// /api/v1/booking-requests/groups/{id}/verify-payment (mirrors
// backend/schemas/quote_group.py::QuoteGroupVerifyPaymentResponse).
// `booking_ids` lists every member's spawned booking, present whenever
// `status` is CONVERTED.
export interface QuoteGroupVerifyPaymentResponse {
  status: QuoteGroupStatus;
  booking_ids: number[];
  message: string;
}

// The admin counterpart of QuoteGroup — the same shape plus the
// customer's identity, mirroring BookingRequestAdmin's own
// customer_name/customer_email pair (mirrors
// backend/schemas/quote_group.py::QuoteGroupAdmin). Returned by the
// admin group GET and by the admin quote-issue route on every
// successful call. `members` includes every member regardless of
// status, DECLINED ones included, each with its own status and
// quoted_total_cents.
export interface QuoteGroupAdmin {
  id: number;
  status: QuoteGroupStatus;
  total_cents: number | null;
  quoted_at: string | null;
  quote_expires_at: string | null;
  customer_name: string | null;
  customer_email: string | null;
  members: BookingRequestAdmin[];
}

// One admin-submitted amount against one live member of the group
// (mirrors backend/schemas/quote_group.py::QuoteGroupLineQuote).
export interface QuoteGroupLineQuote {
  booking_request_id: number;
  quoted_total_cents: number;
}

// Admin request body for POST /admin/quote-groups/{id}/quote — the
// same body serves the initial quote, a revise, and a re-open (mirrors
// backend/schemas/quote_group.py::QuoteGroupQuoteRequest). One line per
// currently-live member; the server 422s on a missing or a
// not-currently-live line.
export interface QuoteGroupQuoteRequest {
  lines: QuoteGroupLineQuote[];
}

// Which level of the listing -> brand -> Terms precedence supplied a resolved
// native cancellation policy (mirrors backend NativeCancellationPolicySource).
export type NativeCancellationPolicySource = 'LISTING' | 'BRAND' | 'TERMS_DEFAULT';

// The cancellation policy resolved server-side for a native listing on a
// detail read (mirrors backend/schemas/native_cancellation_policy.py).
export interface NativeCancellationPolicy {
  summary: string | null;
  body: string | null;
  source: NativeCancellationPolicySource;
}

// The cancellation policy in force when a booking was created (mirrors
// backend/schemas/booking.py::CancellationPolicySnapshotResponse).
export interface CancellationPolicySnapshot {
  display_text: string | null;
  provider: string;
  policy_id: number | null;
  captured_at: string;
}

// A brand as returned by every admin brand route (mirrors
// backend/schemas/brand.py::BrandResponse). Backed by a `Vendor` row
// repurposed as an ownerless attribution entity.
export interface Brand {
  id: number;
  name: string;
  logo_url: string | null;
  link: string | null;
  google_place_id: string | null;
  cancellation_policy_summary?: string | null;
  cancellation_policy_body?: string | null;
}

// Admin request body for creating a brand (mirrors
// backend/schemas/brand.py::BrandCreate). A `name` colliding with an
// existing brand is rejected with 409; a non-https `link` is rejected
// with 422; a `google_place_id` Google reports as not found is rejected
// with 422 (blank clears it to null).
export interface BrandCreate {
  name: string;
  logo_url?: string | null;
  link?: string | null;
  google_place_id?: string | null;
  cancellation_policy_summary?: string | null;
  cancellation_policy_body?: string | null;
}

// Admin request body for partially updating a brand (mirrors
// backend/schemas/brand.py::BrandUpdate). Every field is optional — only
// the fields present in the request are applied.
export interface BrandUpdate {
  name?: string;
  logo_url?: string | null;
  link?: string | null;
  google_place_id?: string | null;
  cancellation_policy_summary?: string | null;
  cancellation_policy_body?: string | null;
}

// One persisted tax line within a TaxProfile (mirrors
// backend/schemas/tax_profile.py::TaxProfileLineResponse). `code` is
// server-derived from `name` on create and immutable afterwards — never
// accepted on input.
export interface TaxProfileLine {
  id: number;
  code: string;
  name: string;
  percentage: number;
  display_order: number;
}

// A reusable, named bundle of tax lines selected per listing via
// accommodations.tax_profile_id (mirrors
// backend/schemas/tax_profile.py::TaxProfileResponse). `listing_count` is
// the number of live listings (`deleted_at IS NULL`) currently
// referencing this profile.
export interface TaxProfile {
  id: number;
  name: string;
  description: string | null;
  lines: TaxProfileLine[];
  listing_count: number;
}

// One tax line within a create/update payload (mirrors
// backend/schemas/tax_profile.py::TaxProfileLineInput). `id` selects an
// existing line to rename/re-percentage; omitting it creates a new line.
// Never carries `code` — the server derives it.
export interface TaxProfileLineInput {
  id?: number | null;
  name: string;
  percentage: number;
}

// Aggregate create/update request body for a tax profile (mirrors
// backend/schemas/tax_profile.py::TaxProfileCreate / TaxProfileUpdate). A
// full replace: any existing line omitted from `lines` is deleted.
export interface TaxProfileWrite {
  name: string;
  description?: string | null;
  lines: TaxProfileLineInput[];
}

// One persisted dated range within a season (mirrors
// backend/schemas/season_calendar.py::SeasonPeriodResponse).
export interface SeasonPeriod {
  id: number;
  from_date: string;
  until_date: string;
}

// One persisted season within a brand's season calendar (mirrors
// backend/schemas/season_calendar.py::SeasonResponse). `rate_count` is the
// number of SeasonRate rows (across every accommodation, including
// unavailable ones) that price this season — the "N rates lost" figure a
// removal confirms against. There is no `rank` field: a season's position
// within the surrounding `seasons` array IS its rank — the first entry is
// the highest-ranked (wins any night its periods share with a lower one).
export interface Season {
  id: number;
  name: string;
  periods: SeasonPeriod[];
  rate_count: number;
}

// One of a vendor's live bookable options, with the ids of this calendar's
// seasons it already carries a rate for (mirrors
// backend/schemas/season_calendar.py::RatedOptionSummary). An option whose
// `rated_season_ids` omits a season has no nightly price for that season
// yet — the signal the calendar editor's save-time safeguard reads.
export interface RatedOptionSummary {
  id: number;
  name: string;
  parent_id: number | null;
  parent_name: string | null;
  rated_season_ids: number[];
}

// Response body for every vendor season-calendar route (mirrors
// backend/schemas/season_calendar.py::BrandSeasonCalendarResponse).
// `seasons` is ordered top-wins-first (i.e. rank descending); `live_options`
// is populated even when `has_calendar` is false, since every live option is
// then trivially missing a rate for every (nonexistent) season.
export interface BrandSeasonCalendar {
  has_calendar: boolean;
  seasons: Season[];
  live_options: RatedOptionSummary[];
}

// One dated range within a season write payload (mirrors
// backend/schemas/season_calendar.py::SeasonPeriodInput). `id` selects an
// existing period to reshape; omitting it (null) creates a new one.
export interface SeasonPeriodInput {
  id: number | null;
  from_date: string;
  until_date: string;
}

// One season within a calendar write payload (mirrors
// backend/schemas/season_calendar.py::SeasonInput). `id` selects an
// existing season to rename/reshape; omitting it creates a new one. Never
// carries a rank — the server derives it entirely from list position.
export interface SeasonInput {
  id: number | null;
  name: string;
  periods: SeasonPeriodInput[];
}

// Request body for PUT /admin/brands/{id}/season-calendar — an aggregate
// full replace of a vendor's calendar in list order (mirrors
// backend/schemas/season_calendar.py::BrandSeasonCalendarWrite). `seasons`
// must carry at least one entry; removing the whole calendar is a DELETE.
export interface BrandSeasonCalendarWrite {
  seasons: SeasonInput[];
}

// How a StayFee amount is computed from a stay's rent subtotal and night
// count (mirrors backend/models/enums.py::StayFeeMode).
export type StayFeeMode = 'percent_of_rent' | 'flat_per_stay' | 'per_night';

// One persisted default stay fee on a brand (mirrors
// backend/schemas/stay_fee.py::StayFeeResponse). `code` is server-derived
// from `name` on create and immutable afterwards — never accepted on
// input. Exactly one of `amount_cents` / `percentage` is set, matching
// `mode` (percent_of_rent carries `percentage`; the other two modes carry
// `amount_cents`).
export interface StayFee {
  id: number;
  code: string;
  name: string;
  mode: StayFeeMode;
  amount_cents: number | null;
  percentage: number | null;
  display_order: number;
}

// One fee within a brand stay-fees write payload (mirrors
// backend/schemas/stay_fee.py::StayFeeInput). `id` selects an existing fee
// to re-shape; omitting it creates a new one. Never carries `code`.
export interface StayFeeInput {
  id: number | null;
  name: string;
  mode: StayFeeMode;
  amount_cents: number | null;
  percentage: number | null;
}

// Request body for PUT /admin/brands/{id}/stay-fees — an aggregate full
// replace of a brand's default fees (mirrors
// backend/schemas/stay_fee.py::BrandStayFeesWrite). An empty `fees` list
// is allowed — it removes every vendor fee.
export interface BrandStayFeesWrite {
  fees: StayFeeInput[];
}

// Response body for every vendor stay-fees route (mirrors
// backend/schemas/stay_fee.py::BrandStayFeesResponse). `fees` is ordered
// (display_order, code).
export interface BrandStayFees {
  fees: StayFee[];
}

export interface ServiceCreate {
  name: string;
  // Admin-set preference for the cart services offer; top-level services only.
  is_offer_highlight?: boolean;
  description: string;
  short_address?: string;
  service_type: string;
  location: string;
  // Belhop's markup on the vendor price, 0-100 with at most 2 decimals.
  platform_markup_percentage: number;
  // Null only when `is_by_request` is true (single-price mode only — a
  // parent with variants is always priced on its children, never here).
  price_per_person?: number | null;
  pricing_unit?: string;
  duration?: number;
  max_capacity: number | null;
  min_capacity?: number;
  vendor_contact?: string;
  google_review_link?: string;
  category?: string;
  included?: InclusionItem[] | null;
  // Attribution brand — mirrors backend schemas/service.py::ServiceBase.vendor_id.
  // Omitted or undefined creates an ownerless (unbranded) listing.
  vendor_id?: number;
  // Bookability discriminator — see Service.is_by_request above. Omit (or
  // send false) for a normally-priced listing; the backend defaults to
  // false when absent.
  is_by_request?: boolean;
  // Same display-only from-price described on
  // Service.by_request_from_price_cents above. Omitted or `null` for a
  // normally-priced or priceless listing; the backend 422s a non-null value
  // sent without `is_by_request: true`.
  by_request_from_price_cents?: number | null;
  // Google Places place_id linking this listing to its Google reviews.
  // Parent-level only — omitted or undefined leaves the listing unlinked.
  google_place_id?: string;
  // Free-text pickup/drop-off hub name. Parent-level only — omitted or
  // undefined leaves the listing with no direction selector.
  transfer_hub_label?: string;
  // Parent-level only; omitted leaves the service on the server default (GUEST_PICKS).
  scheduling_mode?: SchedulingMode;
  // Parent-level only; omitted leaves the listing on the brand/Terms policy.
  cancellation_policy_summary?: string;
  cancellation_policy_body?: string;
  // Billing mechanism and unit-count bounds. Never send `billing_basis: null`
  // (omit it instead; the server rejects an explicit null).
  billing_basis?: BillingBasis;
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
}

// Request body for POST /api/v1/services/{id}/variants.
// Mirrors backend schemas/service.py ServiceVariantCreate.
// parent_id is set server-side; description/location/category — and vendor_id,
// the attribution brand — are always inherited from the parent and cannot be
// overridden here.
export interface ServiceVariantCreate {
  name: string;
  // Relaxed from a bare `number` to accept `null` — a by-request option
  // sends an explicit null price (never a coerced 0) alongside
  // `is_by_request: true`. Mirrors AccommodationVariantCreate's identically
  // nullable `base_price_per_night`.
  price_per_person: number | null;
  pricing_unit?: string;
  duration?: number | null;
  max_capacity?: number | null;
  description?: string;
  included?: InclusionItem[] | null;
  // Admin-controlled option order — the row's position in the repeater at
  // save time. Stamped on every create/update payload; the wire order
  // returned by the variants endpoints is (display_order, id), so hydration
  // never needs a client-side sort.
  display_order?: number;
  // Per-option applicability flag for the hub-relative direction selector;
  // defaults true server-side so a destination-style variant stays
  // directional unless authored otherwise. Mirrors backend
  // schemas/service.py::ServiceVariantCreate.is_hub_transfer (see
  // backend/models/service.py::Service.is_directional_option for the rule).
  is_hub_transfer?: boolean;
  // The option's own billing mechanism and unit-count bounds. Never send
  // `billing_basis: null` (omit it instead; the server rejects an explicit
  // null). Mirrors backend schemas/service.py::ServiceVariantCreate.
  billing_basis?: BillingBasis;
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
  // No fixed price for this option — quoted by an admin per customer.
  // Mirrors backend schemas/service.py::ServiceVariantCreate.is_by_request.
  // Optional (unlike AccommodationVariantCreate's required equivalent) so
  // an already-typed caller that predates this field keeps compiling; the
  // admin authoring form always sends it explicitly.
  is_by_request?: boolean;
}

// Admin request body for updating a service's PARENT fields via the service
// edit form (mirrors backend/schemas/service.py::ServiceUpdate, scoped to
// the fields the edit form actually authors). Like AccommodationUpdatePayload,
// every field here is sent explicitly on every save — the backend's
// partial-PUT semantics treat an omitted key as "leave unchanged" and an
// explicit `null` as "clear" — so making every field required at the type
// level turns a silently-dropped key into a compile error, not a runtime
// bug. In multiple-options mode the parent carries no price authority:
// price_per_person, pricing_unit, and duration are sent as explicit `null`
// (pricing lives on the option rows instead, via ServiceVariantCreate).
// `is_available`, `listing_source`, and `parent_id` are deliberately
// absent — this form never authors them, and the partial PUT must never
// touch them.
//
// The same PUT /services/{id} route also accepts an existing child
// variant's id, taking a ServiceVariantCreate-shaped body to rename/reprice
// that one row without touching the parent-only fields below (name,
// price_per_person, pricing_unit, duration, description, and included are
// the fields the two shapes share) — see apiService.updateService's
// widened parameter type.
export interface ServiceUpdatePayload {
  name: string;
  // Sent explicitly on every save; the API refuses true on a child variant.
  is_offer_highlight: boolean;
  description: string;
  service_type: string;
  location: string;
  // Omitted to leave the stored markup unchanged (the API rejects null), and
  // on a NEXTPAX service, which never authors one.
  platform_markup_percentage?: number;
  max_capacity: number | null;
  category: string;
  included: InclusionItem[];
  vendor_id: number | null;
  price_per_person: number | null;
  pricing_unit: string | null;
  duration: number | null;
  // Bookability discriminator — see Service.is_by_request above. Single-price
  // mode only; sent as `false` in multiple-options mode (a multi-variant
  // parent is never itself by-request). Sent explicitly on every save
  // (required, not optional) per this interface's partial-PUT contract.
  is_by_request: boolean;
  // Same display-only from-price described on
  // Service.by_request_from_price_cents above. Single-price mode only —
  // sent explicitly on every save (required, not optional) per this
  // interface's partial-PUT contract; `null` in multiple-options mode or
  // whenever the toggle above it is off.
  by_request_from_price_cents: number | null;
  // Google Places place_id linking this listing to its Google reviews.
  // Required, with a `null` clear sentinel: an omitted key would leave a
  // previously-stored value unchanged under the backend's partial-PUT
  // semantics, so a blank field must send explicit `null` to actually clear
  // it (mirrors transfer_hub_label below).
  google_place_id: string | null;
  // Free-text pickup/drop-off hub name. Required, with a `null` clear
  // sentinel — presence of this field is the guest-facing To/From selector's
  // trigger, so a blank field must send explicit `null` to actually clear
  // it, not merely omit the key.
  transfer_hub_label: string | null;
  // Parent-level only; a parent write cascades to its live variants.
  scheduling_mode?: SchedulingMode;
  // Explicit `null` clears a stored policy.
  cancellation_policy_summary: string | null;
  cancellation_policy_body: string | null;
  // Billing mechanism and unit-count bounds, sent on every save. Never send
  // `billing_basis: null` (the server rejects an explicit null); `null`
  // bounds clear a stored bound.
  billing_basis?: BillingBasis;
  minimum_unit_count?: number | null;
  maximum_unit_count?: number | null;
}

// Amenities
export interface Amenity {
  id: number;
  name: string;
  category: string;
  icon?: string;
  description?: string;
}

/**
 * A curated catalog amenity as projected onto an `Accommodation` response —
 * distinct from `Amenity` (the catalog row shape, with `id`, used for admin
 * picking) because this is a read-only response projection with no id.
 * Mirrors `backend/schemas/accommodation.py::AmenityDetail`.
 */
export interface AmenityDetail {
  name: string;
  icon?: string | null;
  category?: string | null;
}

// Notifications
export interface NotificationAction {
  label: string;
  onClick: () => void;
}

export interface Notification {
  id: number;
  user_id: number;
  title: string;
  message: string;
  /** Delivery channel — not the visual severity. See `severity`. */
  notification_type: 'email' | 'sms' | 'push' | 'in_app';
  category: 'booking' | 'payment' | 'review' | 'system' | 'marketing';
  /**
   * Visual severity for in-app toasts. Drives the toast's left-border colour
   * + icon glyph in `NotificationToast`. Optional because backend-sourced
   * notifications (booking/payment/etc.) don't surface in the toast layer.
   */
  severity?: 'success' | 'error' | 'warning' | 'info';
  is_read: boolean;
  is_sent: boolean;
  booking_id?: number;
  accommodation_id?: number;
  created_at: string;
  updated_at: string;
  action?: NotificationAction;
}

// Advanced Search
export interface AdvancedSearchFilters {
  location?: string;
  accommodation_type?: string;
  check_in?: string;
  check_out?: string;
  guest_count?: number;
  min_price?: number;
  max_price?: number;
  amenity_ids?: number[];
  amenity_categories?: string[];
  sort_by?: 'relevance' | 'price_low' | 'price_high' | 'rating' | 'newest';
  page?: number;
  limit?: number;
  latitude?: number;
  longitude?: number;
  radius_km?: number;
}

export interface FilterOptions {
  accommodation_types: string[];
  amenities: { [category: string]: Amenity[] };
  price_range: { min: number; max: number };
  popular_locations: string[];
  guest_count_options: number[];
}

/** Inventory a location suggestion is drawn from; mirrors `LocationSuggestionKind`. */
export type LocationSuggestionKind = 'accommodation' | 'service';

/**
 * A single typeahead row for the hero "Where" search. Mirrors
 * `backend/schemas/search.py::LocationSuggestion` — `location` is a distinct
 * location of the requested kind's inventory and `listing_count` is the count
 * of listed rows backing it (empty-query mode ranks by this count).
 */
export interface LocationSuggestion {
  location: string;
  listing_count: number;
}

// Analytics & Admin
export interface AnalyticsOverview {
  period_days: number;
  users: {
    total: number;
    guests: number;
    new_in_period: number;
  };
  bookings: {
    total: number;
    confirmed: number;
    cancelled: number;
    new_in_period: number;
    cancellation_rate: number;
  };
  revenue: {
    total: number;
    period: number;
    average_booking: number;
  };
  properties: {
    total: number;
    active: number;
  };
  reviews: {
    total: number;
    average_rating: number;
    pending_moderation: number;
  };
}

export interface BookingChartData {
  date: string;
  bookings: number;
  revenue: number;
}

// Legacy interfaces for backward compatibility
export interface AuthTokens {
  access_token: string;
  token_type: string;
}

export interface ChatMessage {
  id: number;
  text: string;
  sender: 'user' | 'bot';
  timestamp?: string;
  serviceRecommendations?: Service[];
  accommodationRecommendations?: Accommodation[];
  accommodations?: Accommodation[]; // For backward compatibility with ChatPage
  services?: Service[]; // For service carousel compatibility with ChatPage
  showServiceCards?: boolean;
  type?:
    | 'text'
    | 'service_recommendations'
    | 'accommodation_recommendations'
    | 'booking_confirmation'
    | 'trip_created'
    | 'availability_check';
  bookingDetails?: BookingDetails;
  availabilityInfo?: AvailabilityInfo;
  tripInfo?: TripInfo;
  tax_percentage?: number; // User's preferred tax percentage for chat calculations
}

export interface BookingDetails {
  booking_id: number;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED';
  accommodation: {
    id: number;
    name: string;
    location: string;
  };
  dates: {
    check_in: string;
    check_out: string;
    nights: number;
  };
  pricing: {
    base_price: number;
    service_fee: number;
    taxes: number;
    total_price: number;
  };
  guest_count: number;
}

export interface AvailabilityInfo {
  available: boolean;
  accommodation: {
    id: number;
    name: string;
    location: string;
    max_guests: number;
  };
  dates: {
    check_in: string;
    check_out: string;
    nights: number;
    parsed_description?: string; // Natural language description of parsed dates
  };
  pricing?: {
    base_price: number;
    service_fee: number;
    taxes: number;
    total_price: number;
    price_per_night: number;
  };
  guest_count: number;
  reason?: string;
}

export interface TripInfo {
  id: number;
  title: string;
  destination: string;
  start_date: string;
  end_date: string;
  duration_days: number;
  status: string;
}

export interface SearchFilters {
  location?: string;
  checkIn?: string;
  checkOut?: string;
  guests?: number;
  category?: 'luxury_accommodation' | 'private_home' | 'all';
  minPrice?: number;
  maxPrice?: number;
  amenities?: string[];
  // New service-related filters
  contentType?: 'accommodations' | 'services' | 'filters' | 'all';
  serviceType?:
    'dining' | 'transportation' | 'spa_wellness' | 'activities' | 'shopping' | 'events' | 'all';
  duration?: number; // max duration in minutes
  capacity?: number; // max capacity needed
}

// --- Setup intent response ----------------------------------------------
// Mirrors backend/schemas/payment.py::SetupIntentResponse. Returned by
// POST /api/v1/payments/setup-intent for the native-only cart card-save path.
export interface SetupIntentResponse {
  client_secret: string;
  setup_intent_id: string;
}

// --- Cart checkout request / response -----------------------------------
// Mirrors backend/schemas/booking.py::CartCheckoutLineRequest,
// CartCheckoutRequest, CartCheckoutLineResult, CartCheckoutResponse.

/**
 * `item_type` discriminator shared by a cart checkout request line and its
 * result line. Mirrors `backend/models/cart_checkout.py::CartLineItemType`:
 *   - `'nextpax_property'`     → draft id; routes through the draft-confirm rail.
 *   - `'native_accommodation'` → accommodation id; routes through the native provider.
 *   - `'native_service'`       → service id; routes through the native provider.
 *   - `'native_package'`       → package id; routes through the native provider.
 *   - `'booking_request_accommodation'` / `'booking_request_service'` /
 *     `'booking_request_package'` → accommodation.id / service.id /
 *     package.id (or the booked child tier's id for a tiered package);
 *     routes through the by-request writer and produces a `BookingRequest`,
 *     not a `Booking` — see `CartCheckoutLineRequest.total_price` and
 *     `CartCheckoutLineResult.booking_request_id` below.
 */
export type CartLineItemType =
  | 'native_service'
  | 'native_accommodation'
  | 'nextpax_property'
  | 'native_package'
  | 'booking_request_accommodation'
  | 'booking_request_service'
  | 'booking_request_package';

/**
 * The by-request line kinds — a `CartCheckoutLineRequest`/
 * `CartCheckoutLineResult` line produces a `BookingRequest`, not a
 * `Booking`, and never carries `total_price`. Single frontend source of
 * truth for this classification; mirrors
 * `backend/models/cart_checkout.py::BY_REQUEST_LINE_ITEM_TYPES` and must be
 * kept in step with it. Consumed by `useCartCheckout.submitRequestOnlyCheckout`
 * (the card-less checkout guard) and any other surface that needs to tell a
 * by-request line apart from a priced one by `item_type` alone.
 */
export const BY_REQUEST_LINE_ITEM_TYPES: ReadonlySet<CartLineItemType> = new Set([
  'booking_request_accommodation',
  'booking_request_service',
  'booking_request_package',
]);

/**
 * One item in the cart checkout request.
 *
 * `client_line_reference` is an opaque, client-supplied correlation token
 * (the cart item's own id) echoed back verbatim on the result line so the
 * frontend can resolve a result to the cart item it came from without
 * re-deriving it from `item_type`/`item_id`, which collide for two cart
 * items that share a booked id (e.g. both legs of a round-trip transfer).
 * Optional/nullable — absent on the synthesised NextPax property line and
 * on requests built before this field existed.
 *
 * `transfer_direction` carries a chauffeur-transfer leg's direction for a
 * `native_service` line, mirroring the cart item's own
 * `transferDirection`. `null`/absent for
 * every non-transfer line.
 *
 * `total_price` is required on every priced line (`native_*`/
 * `nextpax_property`) and MUST be omitted on a by-request line
 * (`booking_request_accommodation`/`booking_request_service`/
 * `booking_request_package`) — an admin quote settles the price later. Mirrors
 * `backend/schemas/booking.py::CartCheckoutLineRequest._validate_request_line_shape`,
 * which rejects (422) a payment line with no `total_price` and a
 * by-request line that carries one.
 */
export interface CartCheckoutLineRequest {
  item_type: CartLineItemType;
  item_id: number;
  client_line_reference?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  service_date?: string | null;
  service_time?: string | null;
  /** A fixed-schedule line names its occurrence and sends no date fields. */
  occurrence_id?: number;
  guest_count?: number;
  total_price?: number | null;
  special_requests?: string | null;
  transfer_direction?: TransferDirectionValue | null;
  // Priced-extras selection. Additive — omitted (rather than sent empty)
  // when the item carries none, mirroring `selected_fee_codes`'
  // omit-when-empty convention so a legacy line's wire shape is
  // byte-identical to before this field existed. The native
  // accommodation, service, and package lines carry it, and a by-request
  // cart line (booking_request_accommodation/service/package) carries it
  // when the selection includes a by-request modifier.
  selected_modifiers?: SelectedModifier[];
  // The booked duration/count for a time-unit (hour/30min/day) service.
  // Mirrors backend/schemas/booking.py::CartCheckoutLineRequest.estimated_unit_count.
  // Sent as explicit `null` (not omitted) on a native_service line with
  // no duration, matching this interface's transfer_direction convention
  // above. Only the native_service line ever carries it — accommodation
  // and package lines never send this key.
  estimated_unit_count?: number | null;
}

/**
 * Request body for POST /api/v1/bookings/checkout-cart.
 *
 * `idempotency_key` is a client-generated UUID4 per cart submission.
 * Same-key retries replay completed lines and re-run only failed ones.
 *
 * `consent_text_version` must match the currently-served version; a
 * mismatch returns HTTP 422 before any booking is created.
 *
 * `tos_version` must match the current Terms of Service version
 * (mirrors `constants/legal.ts` `TOS_VERSION`); a mismatch returns HTTP 422
 * before any booking is created, on the same boundary as
 * `consent_text_version` above but for a distinct legal act — ToS
 * acceptance, not authorisation to charge a saved payment method.
 *
 * `saved_payment_method_id` must be a `pm_`-prefixed Stripe PaymentMethod
 * id. A `pi_`-prefixed value is rejected with 422.
 *
 * `consent_text_version` and `saved_payment_method_id` are optional and
 * conditionally required together: both MUST be omitted (or sent as
 * `null`) when every line in `items` is a by-request line
 * (`booking_request_accommodation`/`booking_request_service`/
 * `booking_request_package`) — an all-by-request checkout needs no
 * off-session charge consent — and both
 * are required the moment any priced line (`native_*`/`nextpax_property`)
 * is present, byte-identical to the pre-existing mandatory-field contract.
 * Mirrors
 * `backend/schemas/booking.py::CartCheckoutRequest._validate_payment_fields_match_line_composition`,
 * which rejects (422) either field present without its required pairing.
 *
 * Currency is absent from this request. Each booking's charged currency is
 * resolved server-side from the listing that owns it — native listings use
 * their own `currency` column; NextPax property bookings use the draft's
 * provider-authoritative currency. No client-supplied value may determine
 * the unit of a server-computed charge amount.
 */
export interface CartCheckoutRequest {
  idempotency_key: string;
  consent_text_version?: string | null;
  tos_version: string;
  saved_payment_method_id?: string | null;
  items: CartCheckoutLineRequest[];
}

/**
 * Per-line outcome returned by POST /api/v1/bookings/checkout-cart.
 *
 * `outcome` is `'created'` or `'failed'`.
 * `booking_id` is set on success for a priced line; `booking_request_id` is
 * set on success for a by-request line — a line produces at most one of
 * the two (mirrors `backend/schemas/booking.py::CartCheckoutLineResult` and
 * the `cart_checkout_lines` CHECK
 * `ck_cart_checkout_lines_at_most_one_outcome_ref`). `reason` is set on
 * failure.
 * `client_line_reference` echoes the request line's reference verbatim
 * (see `CartCheckoutLineRequest.client_line_reference`); `null` on legacy
 * rows recorded before this field existed.
 */
export interface CartCheckoutLineResult {
  item_type: CartLineItemType;
  item_id: number;
  outcome: ChargeOutcome;
  booking_id: number | null;
  booking_request_id: number | null;
  reason: string | null;
  client_line_reference?: string | null;
}

/**
 * Response from POST /api/v1/bookings/checkout-cart.
 *
 * `checkout_id` is the CartCheckout.id for this idempotency key.
 * `lines` carries the per-item typed outcome.
 * `all_succeeded` is true only when every line produced a booking.
 */
export interface CartCheckoutResponse {
  checkout_id: number;
  lines: CartCheckoutLineResult[];
  all_succeeded: boolean;
}

/**
 * Per-line charge outcome — mirrors the backend CartCheckoutLineResult.outcome
 * enum values. 'created' means the booking was successfully created; 'failed'
 * means an error occurred for that line (reason is populated).
 */
export type ChargeOutcome = 'created' | 'failed';

// --- Concierge enquiry request / response --------------------------------
// Mirrors backend/schemas/enquiry.py::EnquiryCreate, EnquiryResponse.
// Public, unauthenticated POST /api/v1/enquiries.

/**
 * Wire body for POST /api/v1/enquiries. `company` is the honeypot — a real
 * guest never sees or fills it; a non-empty value is a bot signal the
 * server acts on. Optional fields are omitted rather than sent empty so the
 * server's own defaults apply.
 */
export interface EnquiryCreateRequest {
  name: string;
  email: string;
  description: string;
  arrival?: string;
  departure?: string;
  party_size?: number;
  budget?: string;
  company?: string;
}

/**
 * Response from POST /api/v1/enquiries. `reply_to_email` is the address the
 * server actually normalised and will reply to — render this, not the
 * value the guest typed, since the two can differ (case, whitespace).
 */
export interface EnquiryResponse {
  message: string;
  reply_to_email: string;
}

// --- Partner application (Join page) request / response ------------------
// Mirrors backend/schemas/partner_application.py::PartnerApplicationCreate,
// PartnerApplicationResponse. Public, unauthenticated POST
// /api/v1/partner-applications. Supply-side sibling of the enquiry types
// above — a distinct wire shape, deliberately not reused.

/**
 * Wire body for POST /api/v1/partner-applications. `company` is the
 * honeypot — a real applicant never sees or fills it; a non-empty value is
 * a bot signal the server acts on. Optional fields are omitted rather than
 * sent empty so the server's own defaults apply.
 */
export interface PartnerApplicationCreateRequest {
  name: string;
  email: string;
  category: string;
  business_name?: string;
  phone?: string;
  website?: string;
  message?: string;
  company?: string;
}

/**
 * Response from POST /api/v1/partner-applications. `reply_to_email` is the
 * address the server actually normalised and will reply to — render this,
 * not the value the applicant typed, since the two can differ (case,
 * whitespace).
 */
export interface PartnerApplicationResponse {
  message: string;
  reply_to_email: string;
}

/**
 * Response from the admin listing-removal endpoints (DELETE
 * /api/v1/accommodations/{id}, DELETE /api/v1/services/{id}). Mirrors
 * backend/schemas/common.py::ListingRemovalResponse. `affected_bookings` is
 * the count of in-flight bookings against the removed listing that remain
 * honoured rather than blocking the removal.
 */
export interface ListingRemovalResponse {
  message: string;
  affected_bookings: number;
}

// An accommodation's exact point as the admin location routes return it
// (mirrors backend schemas/accommodation_location.py). Both values are
// present, or both null when the listing is unplaced.
export interface AccommodationLocation {
  latitude: number | null;
  longitude: number | null;
}

// Hero slots: the admin-chosen listing and photo for a guest-facing hero.
// Mirrors backend/schemas/hero_slot.py.
export type HeroSlotName = 'discover' | 'stays' | 'services';

export interface HeroPick {
  slot: HeroSlotName;
  listing_id: number;
  listing_slug?: string;
  listing_name: string;
  location: string;
  image_url: string;
  focal_x: number;
  focal_y: number;
}

export interface PublicHeroPicks {
  discover: HeroPick | null;
  stays: HeroPick | null;
  services: HeroPick | null;
}

export interface HeroSlotUpdate {
  listing_id: number;
  image_id: number;
  focal_x: number;
  focal_y: number;
}

export interface AdminHeroSlot {
  slot: HeroSlotName;
  listing_id: number | null;
  image_id: number | null;
  is_live: boolean;
  pick: HeroPick | null;
}
